import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MissingFieldsAlert } from '@/components/MissingFieldsAlert';
import { Link as RouterLink } from 'react-router-dom';
import { Plus, X, Pencil, CheckCircle2, Check, Clock, FileText, File, ListTodo, MessageSquareText, ArrowRight, AlertCircle, CalendarDays, Trash2, User, Calendar, Upload, Download, Paperclip, Eye, Zap, RefreshCw, Building2, ChevronDown, ChevronRight, ExternalLink, Flame, Landmark } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextDisplay from '@/components/RichTextDisplay';
import { FileUpload } from '@/components/FileUpload';
import { DocumentPreviewModal } from '@/components/DocumentPreviewModal';
import { QuickLogTimeSheet } from '@/components/QuickLogTimeSheet';
import { sortSections, groupSectionsByTag, SOURCE_TAG_OPTIONS } from '@/lib/sectionSort';
import api from '@/lib/api';
import CreateTodoFromNoteDialog from './CreateTodoFromNoteDialog';
import MeetingNoteTodosBadge from '@/components/MeetingNoteTodosBadge';
import MeetingNoteCreateForm from './MeetingNoteCreateForm';
import MeetingNoteEditForm from './MeetingNoteEditForm';

const PROJECT_STATUSES = ['Intake', 'Waiting on Client', 'Filing', 'Review', 'Approved', 'Completed', 'Archived'];
const SECTION_STATUSES = ['Not Started', 'In Progress', 'Filed', 'Approved', 'Completed', 'On Hold', 'Rejected', 'Expired'];
const FDNY_PERMIT_TYPES = ['Fuel', 'Generator/Battery', 'HVAC/Heater', 'Explosives/Fireworks/Pyrotechnics', 'Open Flame', 'Portable Fire Extinguisher Tag', 'Violation', 'Other'];
const FDNY_STATUSES = ['Active', 'Expired', 'Pending', 'Denied'];
const FDNY_PORTAL_URL = 'https://fires.fdnycloud.org/';
const FDNY_EMPTY_FORM = { record_type: 'enforcement_violation', record_number: '', notes: '', fdny_permit_type: '', status: 'Active', issue_date: '', expiration_date: '' };

// Other Jurisdiction (non-NYC municipal) — fully manual, NO public dataset / fetch.
const OTHER_JURISDICTIONS = ['Town of Brookhaven', 'Town of Babylon', 'Town of Southampton', 'Town of Oyster Bay', 'Town of Huntington', 'Town of Hempstead', 'Village of Williston Park', 'Village of East Rockaway', 'Suffolk County DPW', 'Other'];
const OJ_STATUSES = ['Active', 'Expired', 'Pending', 'Denied'];
const OJ_EMPTY_FORM = { jurisdiction: '', jurisdiction_other: '', record_type: '', record_number: '', status: 'Active', issue_date: '', expiration_date: '', notes: '' };

// Jurisdiction (carried forward from the contract at project creation; freely editable here).
const PROJECT_JURISDICTION_OPTIONS = [
  { value: 'nyc', label: 'NYC' },
  { value: 'nassau_county', label: 'Nassau County' },
  { value: 'suffolk_county', label: 'Suffolk County' },
  { value: 'westchester', label: 'Westchester' },
  { value: 'other', label: 'Other' },
];

// Unique id for a discovered record across all seven sources (dedupe key per source).
const discoverIdOf = (it) => it.job_number || it.violation_number || it.device_number || it.tracking_number || it.control_no || it.violationid || '';

// Status-based count breakdown for the Elevator/Boiler/Facade lists (sorted desc).
const statusBreakdown = (records, statusOf) => {
  const m = {};
  records.forEach(r => { const s = (statusOf(r) || 'Unknown').trim() || 'Unknown'; m[s] = (m[s] || 0) + 1; });
  return Object.entries(m).sort((a, b) => b[1] - a[1]);
};
const statusToneClass = (s) => {
  const t = (s || '').toLowerCase();
  if (t.includes('active') || t.includes('accept') || t.includes('approv')) return 'text-emerald-400';
  if (t.includes('expir') || t.includes('reject') || t.includes('deni') || t.includes('fail') || t.includes('no report')) return 'text-rose-400';
  return 'text-muted-foreground';
};
const statusBadgeClass = (s) => {
  const t = (s || '').toLowerCase();
  if (t.includes('active') || t.includes('accept') || t.includes('approv')) return 'border-emerald-500/40 text-emerald-400';
  if (t.includes('expir') || t.includes('reject') || t.includes('deni') || t.includes('fail') || t.includes('no report')) return 'border-rose-500/40 text-rose-400';
  return 'border-border text-muted-foreground';
};
const SECTION_EVENT_TYPES = ['Filing Expiration', 'Permit Expiration', 'Approval Expiration', 'Follow Up', 'Renewal Deadline', 'Other Deadline'];

const statusColors = {
  'Intake': 'bg-blue-500/15 text-blue-400',
  'Waiting on Client': 'bg-amber-500/15 text-amber-400',
  'Filing': 'bg-violet-500/15 text-violet-400',
  'Review': 'bg-cyan-500/15 text-cyan-400',
  'Approved': 'bg-emerald-500/15 text-emerald-400',
  'Completed': 'bg-teal-500/15 text-teal-400',
  'Archived': 'bg-zinc-500/15 text-zinc-400',
};

const DOB_STATUS_COLORS = {
  'Permit Entire': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'Permit Issued': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'PERMIT ISSUED - ENTIRE JOB/WORK': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'PERMIT ISSUED - PARTIAL JOB': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'Permitted': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'Approved': 'bg-green-500/15 text-green-400 border-green-500/30',
  'PLAN EXAM - APPROVED': 'bg-green-500/15 text-green-400 border-green-500/30',
  'PAA Approved': 'bg-green-500/15 text-green-400 border-green-500/30',
  'LOC Issued': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  'Complete': 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  'SIGNED OFF': 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  'Signed off': 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  'COMPLETED': 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  'Objections': 'bg-red-500/15 text-red-400 border-red-500/30',
  'PLAN EXAM - DISAPPROVED': 'bg-red-500/15 text-red-400 border-red-500/30',
  'Rejected': 'bg-red-500/15 text-red-400 border-red-500/30',
  'Expired': 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  'Cancel': 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  'Filing Withdrawn': 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  'Withdrawn': 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  'Plan Examiner Review': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'PLAN EXAM - IN PROCESS': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'Pre-Filing': 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  'PRE-FILING': 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  'Incomplete': 'bg-orange-500/15 text-orange-400 border-orange-500/30',
};
const getDobStatusColor = (status) => DOB_STATUS_COLORS[status] || 'bg-sky-500/15 text-sky-400 border-sky-500/30';

const openDobNowJobSummary = async (jobNumber) => {
  if (!jobNumber) return;
  const jn = String(jobNumber).trim();
  if (!jn) return;
  // For DOB NOW filings the job_filing_number includes a suffix like "-I1" /
  // "-A1". The DOBNOW Job Summary page is keyed off the base job number, so
  // strip the suffix before navigating.
  const baseJob = jn.split('-')[0];
  try {
    const r = await api.get(`/dob/deep-link/${encodeURIComponent(baseJob)}`);
    if (r.data?.url) {
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
      return;
    }
  } catch { /* fall through to direct URL */ }
  // Fallback: open the public portal homepage so user can search manually
  window.open(`https://a810-dobnow.nyc.gov/publish/index.html#!/COVJobSummary?JobId=${encodeURIComponent(baseJob)}&UserId=PublicPortalUser`, '_blank', 'noopener,noreferrer');
};

const TIMELINE_CATEGORIES = {
  permitted: { statuses: ['Permit Entire', 'Permit Issued', 'Permitted', 'PERMIT ISSUED - ENTIRE JOB/WORK', 'PERMIT ISSUED - PARTIAL JOB'], dot: 'bg-emerald-400', badge: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  approved: { statuses: ['Approved', 'PLAN EXAM - APPROVED', 'PAA Approved', 'LOC Issued', 'CO Issued'], dot: 'bg-green-400', badge: 'bg-green-500/15 text-green-400 border-green-500/30' },
  completed: { statuses: ['Complete', 'SIGNED OFF', 'Signed off', 'COMPLETED', 'APPLICATION PROCESSED - ENTIRE', 'APPLICATION PROCESSED - NO PLAN EXAM'], dot: 'bg-teal-400', badge: 'bg-teal-500/15 text-teal-400 border-teal-500/30' },
  issues: { statuses: ['Objections', 'PLAN EXAM - DISAPPROVED', 'Rejected', 'Incomplete', 'QA Failed', 'SUSPENDED'], dot: 'bg-red-400', badge: 'bg-red-500/15 text-red-400 border-red-500/30' },
  withdrawn: { statuses: ['Expired', 'Cancel', 'Filing Withdrawn', 'Withdrawn', 'Revoked'], dot: 'bg-zinc-400', badge: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30' },
  review: { statuses: ['Plan Examiner Review', 'PLAN EXAM - IN PROCESS', 'Pre-Filing', 'PRE-FILING', 'Pending Payment'], dot: 'bg-amber-400', badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
};
function _getTimelineCategory(status) {
  for (const cat of Object.values(TIMELINE_CATEGORIES)) {
    if (cat.statuses.includes(status)) return cat;
  }
  return { dot: 'bg-sky-400', badge: 'bg-sky-500/15 text-sky-400 border-sky-500/30' };
}

/** Isolated Source Tag P.O. card - manages its own local state */
function SourceTagPoCard({ projectId, tags, initialPos }) {
  const [localPos, setLocalPos] = useState(initialPos);
  const prevProjectId = useRef(projectId);

  useEffect(() => {
    if (projectId !== prevProjectId.current) {
      prevProjectId.current = projectId;
      setLocalPos(initialPos);
    }
  }, [projectId, initialPos]);

  const sortedTags = [...tags].sort((a, b) => {
    const ao = a === 'Original Contract' ? 0 : parseInt(a.match(/\d+/)?.[0] || '999');
    const bo = b === 'Original Contract' ? 0 : parseInt(b.match(/\d+/)?.[0] || '999');
    return ao - bo;
  });

  const savePo = async (tag, value) => {
    const newPos = { ...localPos, [tag]: value };
    try { await api.put(`/projects/${projectId}`, { source_tag_pos: newPos }); } catch { /* fail silently — best-effort write */ }
  };

  return (
    <Card className="border-border bg-card/50 shadow-none" data-testid="source-tag-po-card">
      <CardContent className="p-3">
        <p className="text-xs font-semibold text-muted-foreground mb-2">P.O. Numbers by Source Tag</p>
        <div className="space-y-1.5">
          {sortedTags.map(tag => (
            <div key={tag} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-32 shrink-0 truncate">{tag}</span>
              <Input
                placeholder="P.O. number"
                value={localPos[tag] || ''}
                onChange={e => setLocalPos(prev => ({ ...prev, [tag]: e.target.value }))}
                onBlur={e => savePo(tag, e.target.value)}
                className="bg-background border-border text-xs h-7 flex-1"
                data-testid={`source-tag-po-${tag.replace(/\s/g, '-')}`}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function ProjectDetailContent({ selected, users, onRefresh, onProjectsRefresh, initialTab, onBack }) {
  const [statusText, setStatusText] = useState('');
  const [actionItems, setActionItems] = useState('');
  const [todoFormOpen, setTodoFormOpen] = useState(false);
  const [todoForm, setTodoForm] = useState({ title: '', description: '', contract_section_id: '', assigned_to: '', priority: 'Medium', due_date: '' });
  const [editProjectName, setEditProjectName] = useState(false);
  const [editProjectNumber, setEditProjectNumber] = useState(false);
  const [projectNameVal, setProjectNameVal] = useState('');
  const [projectNumberVal, setProjectNumberVal] = useState('');
  const [editStartDate, setEditStartDate] = useState(false);
  const [startDateVal, setStartDateVal] = useState('');
  const [editAddress, setEditAddress] = useState(false);
  const [addressVal, setAddressVal] = useState('');
  const [editTodo, setEditTodo] = useState(null);
  const [editTodoForm, setEditTodoForm] = useState({});
  const [projectReminders, setProjectReminders] = useState([]);
  const [editProjectSection, setEditProjectSection] = useState(null);
  const [editProjSectionForm, setEditProjSectionForm] = useState({});
  const [projSectionEventTypePrompt, setProjSectionEventTypePrompt] = useState(false);
  const [projSectionPendingEventType, setProjSectionPendingEventType] = useState('Filing Expiration');
  // Derived directly from the loaded project — keeping a local copy caused
  // stale data to bleed between projects when switching panels.
  const meetingNotes = selected?.meeting_notes || [];
  const [meetingNoteFormOpen, setMeetingNoteFormOpen] = useState(false);
  // Shared between Status Log "Add Status + Log Time" and Meeting Notes
  // "Save Note + Log Time" — both routes open the same QuickLogTimeSheet
  // since the resulting timesheet shape is identical (project + section).
  const [quickLogOpen, setQuickLogOpen] = useState(false);
  const [quickLogNotes, setQuickLogNotes] = useState('');
  const [quickLogStatusId, setQuickLogStatusId] = useState(null);
  const [quickLogSurface, setQuickLogSurface] = useState('status_log_log_time');
  const [editMeetingNote, setEditMeetingNote] = useState(null);
  const [todoFromNote, setTodoFromNote] = useState(null);
  const [activeTab, setActiveTab] = useState('sections');
  const [highlightNoteId, setHighlightNoteId] = useState(null);
  const [editStatusEntry, setEditStatusEntry] = useState(null);
  const [editStatusForm, setEditStatusForm] = useState({});
  const [projectAttachments, setProjectAttachments] = useState([]);
  const [attachFormOpen, setAttachFormOpen] = useState(false);
  const [attachName, setAttachName] = useState('');
  const [attachUploaded, setAttachUploaded] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [dobStatuses, setDobStatuses] = useState({});
  const [dobRefreshing, setDobRefreshing] = useState(false);
  const [dobJobNumbers, setDobJobNumbers] = useState([]);
  const [newDobJob, setNewDobJob] = useState('');
  const [dobJobSaving, setDobJobSaving] = useState(false);
  const [projectFilingOptions, setProjectFilingOptions] = useState([]);
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [profileTimelineExpanded, setProfileTimelineExpanded] = useState(true);
  const [pmEditing, setPmEditing] = useState(false);

  // BIN + BIN-based bulk discovery (Prompt 2)
  const [binVal, setBinVal] = useState('');
  const [editBin, setEditBin] = useState(false);
  const [binSaving, setBinSaving] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoverResults, setDiscoverResults] = useState({ dob_violations: [], dob_filings: [], ecb_violations: [], elevator: [], boiler: [], facade: [], hpd: [] });
  const [discoverWarnings, setDiscoverWarnings] = useState({});
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [addingSelected, setAddingSelected] = useState(false);

  // DOT permits (manual entry by permit number; status/expiration are fetch-only)
  const [dotPermits, setDotPermits] = useState([]);
  const [dotForm, setDotForm] = useState({ permit_number: '', cost: '' });
  const [dotAdding, setDotAdding] = useState(false);
  const [dotRefreshingId, setDotRefreshingId] = useState(null);

  // FDNY records (fully manual; NO live FDNY data source exists for either type)
  const [fdnyRecords, setFdnyRecords] = useState([]);
  const [fdnyForm, setFdnyForm] = useState(FDNY_EMPTY_FORM);
  const [fdnyAdding, setFdnyAdding] = useState(false);
  const [fdnyEditingId, setFdnyEditingId] = useState(null);
  const [fdnyEditForm, setFdnyEditForm] = useState(null);
  const [fdnyExpandedId, setFdnyExpandedId] = useState(null);

  // Other Jurisdiction records (non-NYC municipal; fully manual, NO fetch ever)
  const [ojRecords, setOjRecords] = useState([]);
  const [ojForm, setOjForm] = useState(OJ_EMPTY_FORM);
  const [ojAdding, setOjAdding] = useState(false);
  const [ojEditingId, setOjEditingId] = useState(null);
  const [ojEditForm, setOjEditForm] = useState(null);
  const [ojExpandedId, setOjExpandedId] = useState(null);

  const updateProjectManagers = async (pm_ids) => {
    try {
      await api.put(`/projects/${selected.id}`, { project_manager_ids: pm_ids });
      toast.success('Project Managers updated');
      refreshSelected();
      onProjectsRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update PMs');
    }
  };

  const addProjectManager = (uid) => {
    const current = (selected?.project_manager_ids) || [];
    if (!uid || current.includes(uid)) return;
    updateProjectManagers([...current, uid]);
  };
  const removeProjectManager = (uid) => {
    const current = (selected?.project_manager_ids) || [];
    updateProjectManagers(current.filter(id => id !== uid));
  };

  // Jurisdiction (carried from contract at creation; freely editable here).
  const [projJurOther, setProjJurOther] = useState(selected?.jurisdiction_other || '');
  useEffect(() => { setProjJurOther(selected?.jurisdiction_other || ''); }, [selected?.id]);
  const saveProjectJurisdiction = async (next) => {
    try {
      await api.put(`/projects/${selected.id}`, { jurisdiction: next });
      refreshSelected();
      onProjectsRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update jurisdiction'); }
  };
  const toggleProjectJurisdiction = (val) => {
    const cur = Array.isArray(selected.jurisdiction) ? selected.jurisdiction : [];
    saveProjectJurisdiction(cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val]);
  };
  const saveProjectJurisdictionOther = async (val) => {
    try { await api.put(`/projects/${selected.id}`, { jurisdiction_other: val }); refreshSelected(); }
    catch (e) { toast.error('Failed to update jurisdiction'); }
  };

  
  // Load action item reminders when project changes
  const fetchReminders = useCallback(async () => {
    if (!selected?.id) return;
    try {
      const r = await api.get(`/action-item-reminders?project_id=${selected.id}`);
      setProjectReminders(r.data);
    } catch { setProjectReminders([]); }
  }, [selected?.id]);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  // When the panel opens with an initialTab (e.g. via deep-link from the
  // Action Items page → Status Log), select that tab. Falls back to
  // 'sections' (the default) on each fresh open.
  useEffect(() => {
    setActiveTab(initialTab || 'sections');
  }, [initialTab, selected?.id]);

  const fetchAttachments = useCallback(async () => {
    if (!selected?.id) return;
    try {
      const r = await api.get(`/documents/by-project/${selected.id}`);
      setProjectAttachments(r.data);
    } catch { setProjectAttachments([]); }
  }, [selected?.id]);

  useEffect(() => { fetchAttachments(); }, [fetchAttachments]);

  const fetchDobStatuses = useCallback(async () => {
    if (!selected?.id) return;
    try {
      const r = await api.get(`/dob/project/${selected.id}`);
      setDobStatuses(r.data);
      setDobJobNumbers(r.data.job_numbers || selected.dob_job_numbers || []);
    } catch { setDobStatuses({}); }
  }, [selected?.id, selected?.dob_job_numbers]);

  useEffect(() => { fetchDobStatuses(); }, [fetchDobStatuses]);

  const fetchProjectFilingOptions = useCallback(async () => {
    if (!selected?.id) return;
    try {
      const r = await api.get(`/dob/project-filings/${selected.id}`);
      setProjectFilingOptions(r.data.filing_options || []);
    } catch { setProjectFilingOptions([]); }
  }, [selected?.id]);

  useEffect(() => { fetchProjectFilingOptions(); }, [fetchProjectFilingOptions]);

  const fetchDotPermits = useCallback(async () => {
    if (!selected?.id) { setDotPermits([]); return; }
    try {
      const r = await api.get(`/projects/${selected.id}/dot-permits`);
      setDotPermits(r.data || []);
    } catch { setDotPermits([]); }
  }, [selected?.id]);

  useEffect(() => { fetchDotPermits(); }, [fetchDotPermits]);

  const addDotPermit = async () => {
    const pn = (dotForm.permit_number || '').trim();
    if (!pn) { toast.error('Permit number is required'); return; }
    setDotAdding(true);
    try {
      await api.post(`/projects/${selected.id}/dot-permits`, {
        permit_number: pn,
        cost: dotForm.cost === '' ? null : Number(dotForm.cost),
      });
      setDotForm({ permit_number: '', cost: '' });
      toast.success('DOT permit added');
      fetchDotPermits();
    } catch { toast.error('Failed to add permit'); }
    setDotAdding(false);
  };

  const refreshDotPermit = async (id) => {
    setDotRefreshingId(id);
    try { await api.post(`/dot-permits/${id}/fetch`); await fetchDotPermits(); }
    catch { toast.error('Refresh failed'); }
    setDotRefreshingId(null);
  };

  const deleteDotPermit = async (id) => {
    try { await api.delete(`/dot-permits/${id}`); setDotPermits(prev => prev.filter(p => p.id !== id)); }
    catch { toast.error('Failed to delete permit'); }
  };

  const dotExpClass = (p) => {
    if (!p.expiration_date) return 'text-muted-foreground';
    const exp = new Date(p.expiration_date);
    if (isNaN(exp.getTime())) return 'text-muted-foreground';
    const days = (exp.getTime() - Date.now()) / 86400000;
    if (days < 0 || p.status === 'Expired') return 'text-rose-400';
    if (days <= 30) return 'text-amber-400';
    return 'text-foreground';
  };

  const dotStatusClass = (s) => (
    s === 'Active' ? 'border-emerald-500/40 text-emerald-400'
    : s === 'Expired' ? 'border-rose-500/40 text-rose-400'
    : s === 'Pending Renewal' ? 'border-amber-500/40 text-amber-400'
    : 'border-border text-muted-foreground'
  );

  // Shared expiration flag for DOT Permits + FDNY permit records (frontend-only, no fetch).
  // Returns 'expired' | 'soon' | null. Within 30 days (incl. already past) = highlight.
  const expiryFlag = (expiration_date, status) => {
    if (!expiration_date) return null;
    const exp = new Date(expiration_date);
    if (isNaN(exp.getTime())) return null;
    const days = (exp.getTime() - Date.now()) / 86400000;
    if (days < 0 || status === 'Expired') return 'expired';
    if (days <= 30) return 'soon';
    return null;
  };
  const expiryTag = (flag, testid) => {
    if (!flag) return null;
    const expired = flag === 'expired';
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${expired ? 'bg-rose-500/15 text-rose-400 border border-rose-500/30' : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'}`} data-testid={testid}>
        <AlertCircle className="h-2.5 w-2.5" /> {expired ? 'Expired' : 'Expiring Soon'}
      </span>
    );
  };
  const expiryBorderClass = (flag) => (
    flag === 'expired' ? 'border-l-2 border-l-rose-500' : flag === 'soon' ? 'border-l-2 border-l-amber-500' : ''
  );

  // ---- FDNY records (manual only; no fetch ever) ----
  const fetchFdnyRecords = useCallback(async () => {
    if (!selected?.id) { setFdnyRecords([]); return; }
    try {
      const r = await api.get(`/projects/${selected.id}/fdny-records`);
      setFdnyRecords(r.data || []);
    } catch { setFdnyRecords([]); }
  }, [selected?.id]);

  useEffect(() => { fetchFdnyRecords(); }, [fetchFdnyRecords]);

  const addFdnyRecord = async () => {
    const num = (fdnyForm.record_number || '').trim();
    if (!num) { toast.error(fdnyForm.record_type === 'enforcement_violation' ? 'Violation number is required' : 'Record number is required'); return; }
    if (fdnyForm.record_type === 'permit_other' && !fdnyForm.fdny_permit_type) { toast.error('Select a permit/record type'); return; }
    setFdnyAdding(true);
    try {
      const payload = { record_type: fdnyForm.record_type, record_number: num, notes: fdnyForm.notes || null };
      if (fdnyForm.record_type === 'permit_other') {
        payload.fdny_permit_type = fdnyForm.fdny_permit_type;
        payload.status = fdnyForm.status;
        payload.issue_date = fdnyForm.issue_date || null;
        payload.expiration_date = fdnyForm.expiration_date || null;
      }
      await api.post(`/projects/${selected.id}/fdny-records`, payload);
      setFdnyForm({ ...FDNY_EMPTY_FORM, record_type: fdnyForm.record_type });
      toast.success('FDNY record added');
      fetchFdnyRecords();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add record'); }
    setFdnyAdding(false);
  };

  const startEditFdny = (rec) => {
    setFdnyEditingId(rec.id);
    setFdnyEditForm({
      record_number: rec.record_number || '',
      notes: rec.notes || '',
      fdny_permit_type: rec.fdny_permit_type || '',
      status: rec.status || 'Active',
      issue_date: (rec.issue_date || '').split('T')[0] || '',
      expiration_date: (rec.expiration_date || '').split('T')[0] || '',
    });
  };

  const saveEditFdny = async (rec) => {
    const num = (fdnyEditForm.record_number || '').trim();
    if (!num) { toast.error('Record number is required'); return; }
    if (rec.record_type === 'permit_other' && !fdnyEditForm.fdny_permit_type) { toast.error('Select a permit/record type'); return; }
    try {
      const payload = { record_number: num, notes: fdnyEditForm.notes || null };
      if (rec.record_type === 'permit_other') {
        payload.fdny_permit_type = fdnyEditForm.fdny_permit_type;
        payload.status = fdnyEditForm.status;
        payload.issue_date = fdnyEditForm.issue_date || null;
        payload.expiration_date = fdnyEditForm.expiration_date || null;
      }
      await api.put(`/fdny-records/${rec.id}`, payload);
      setFdnyEditingId(null);
      setFdnyEditForm(null);
      toast.success('FDNY record updated');
      fetchFdnyRecords();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update record'); }
  };

  const deleteFdnyRecord = async (id) => {
    try { await api.delete(`/fdny-records/${id}`); setFdnyRecords(prev => prev.filter(r => r.id !== id)); }
    catch { toast.error('Failed to delete record'); }
  };

  const fdnyStatusClass = (s) => (
    s === 'Active' ? 'border-emerald-500/40 text-emerald-400'
    : s === 'Expired' ? 'border-rose-500/40 text-rose-400'
    : s === 'Pending' ? 'border-amber-500/40 text-amber-400'
    : s === 'Denied' ? 'border-zinc-500/40 text-zinc-400'
    : 'border-border text-muted-foreground'
  );

  const fdnyExpClass = (rec) => {
    if (!rec.expiration_date) return 'text-muted-foreground';
    const exp = new Date(rec.expiration_date);
    if (isNaN(exp.getTime())) return 'text-muted-foreground';
    const days = (exp.getTime() - Date.now()) / 86400000;
    if (days < 0 || rec.status === 'Expired') return 'text-rose-400';
    if (days <= 30) return 'text-amber-400';
    return 'text-foreground';
  };

  // ---- Other Jurisdiction records (non-NYC municipal; manual only, no fetch ever) ----
  const fetchOjRecords = useCallback(async () => {
    if (!selected?.id) { setOjRecords([]); return; }
    try {
      const r = await api.get(`/projects/${selected.id}/other-jurisdiction-records`);
      setOjRecords(r.data || []);
    } catch { setOjRecords([]); }
  }, [selected?.id]);

  useEffect(() => { fetchOjRecords(); }, [fetchOjRecords]);

  const ojJurisdictionLabel = (rec) => (rec.jurisdiction === 'Other' ? (rec.jurisdiction_other || 'Other') : rec.jurisdiction);

  const addOjRecord = async () => {
    if (!ojForm.jurisdiction) { toast.error('Select a jurisdiction'); return; }
    if (ojForm.jurisdiction === 'Other' && !(ojForm.jurisdiction_other || '').trim()) { toast.error('Enter the jurisdiction name'); return; }
    if (!(ojForm.record_type || '').trim()) { toast.error('Record type / description is required'); return; }
    const num = (ojForm.record_number || '').trim();
    if (!num) { toast.error('Record number is required'); return; }
    setOjAdding(true);
    try {
      await api.post(`/projects/${selected.id}/other-jurisdiction-records`, {
        jurisdiction: ojForm.jurisdiction,
        jurisdiction_other: ojForm.jurisdiction === 'Other' ? ojForm.jurisdiction_other.trim() : null,
        record_type: ojForm.record_type.trim(),
        record_number: num,
        status: ojForm.status,
        issue_date: ojForm.issue_date || null,
        expiration_date: ojForm.expiration_date || null,
        notes: ojForm.notes || null,
      });
      setOjForm(OJ_EMPTY_FORM);
      toast.success('Other jurisdiction record added');
      fetchOjRecords();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add record'); }
    setOjAdding(false);
  };

  const startEditOj = (rec) => {
    setOjEditingId(rec.id);
    setOjEditForm({
      jurisdiction: rec.jurisdiction || '',
      jurisdiction_other: rec.jurisdiction_other || '',
      record_type: rec.record_type || '',
      record_number: rec.record_number || '',
      status: rec.status || 'Active',
      issue_date: (rec.issue_date || '').split('T')[0] || '',
      expiration_date: (rec.expiration_date || '').split('T')[0] || '',
      notes: rec.notes || '',
    });
  };

  const saveEditOj = async (rec) => {
    if (!ojEditForm.jurisdiction) { toast.error('Select a jurisdiction'); return; }
    if (ojEditForm.jurisdiction === 'Other' && !(ojEditForm.jurisdiction_other || '').trim()) { toast.error('Enter the jurisdiction name'); return; }
    if (!(ojEditForm.record_type || '').trim()) { toast.error('Record type / description is required'); return; }
    const num = (ojEditForm.record_number || '').trim();
    if (!num) { toast.error('Record number is required'); return; }
    try {
      await api.put(`/other-jurisdiction-records/${rec.id}`, {
        jurisdiction: ojEditForm.jurisdiction,
        jurisdiction_other: ojEditForm.jurisdiction === 'Other' ? ojEditForm.jurisdiction_other.trim() : null,
        record_type: ojEditForm.record_type.trim(),
        record_number: num,
        status: ojEditForm.status,
        issue_date: ojEditForm.issue_date || null,
        expiration_date: ojEditForm.expiration_date || null,
        notes: ojEditForm.notes || null,
      });
      setOjEditingId(null);
      setOjEditForm(null);
      toast.success('Record updated');
      fetchOjRecords();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update record'); }
  };

  const deleteOjRecord = async (id) => {
    try { await api.delete(`/other-jurisdiction-records/${id}`); setOjRecords(prev => prev.filter(r => r.id !== id)); }
    catch { toast.error('Failed to delete record'); }
  };

  const [actionHourEntryId, setActionHourEntryId] = useState(null);
  const [actionHourLoading, setActionHourLoading] = useState(false);

  const checkActionHour = useCallback(async () => {
    if (!selected?.id) return;
    try {
      const r = await api.get('/action-hour');
      const entry = (r.data || []).find(e => e.project_id === selected.id);
      setActionHourEntryId(entry ? entry.id : null);
    } catch { setActionHourEntryId(null); }
  }, [selected?.id]);

  useEffect(() => { checkActionHour(); }, [checkActionHour]);

  // Scroll + briefly ring the targeted meeting note when the user follows a
  // "Source" link from a task card.
  useEffect(() => {
    if (activeTab !== 'meeting-notes' || !highlightNoteId) return;
    const el = document.getElementById(`mn-${highlightNoteId}`);
    if (!el) return;
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
        setHighlightNoteId(null);
      }, 2000);
    }, 150);
    return () => clearTimeout(timer);
  }, [activeTab, highlightNoteId, meetingNotes.length]);
  

  const toggleActionHour = async () => {
    setActionHourLoading(true);
    try {
      if (actionHourEntryId) {
        await api.delete(`/action-hour/${actionHourEntryId}`);
        toast.success('Removed from Action Hour board');
        setActionHourEntryId(null);
      } else {
        await api.post('/action-hour', { project_id: selected.id });
        toast.success('Added to Action Hour board');
        checkActionHour();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating Action Hour'); }
    setActionHourLoading(false);
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const r = await api.get(`/projects/${selected.id}`);
    onRefresh(r.data);
  };

  const refreshDobProject = async () => {
    if (!selected?.id) return;
    setDobRefreshing(true);
    try {
      await api.post(`/dob/refresh-project/${selected.id}`);
      await fetchDobStatuses();
      await fetchProjectFilingOptions();
      toast.success('DOB statuses refreshed');
    } catch { toast.error('Failed to refresh DOB statuses'); }
    setDobRefreshing(false);
  };

  const addDobJobNumber = async () => {
    const jn = newDobJob.trim();
    if (!jn) return;
    if (dobJobNumbers.includes(jn)) { toast.error('Job number already added'); return; }
    const updated = [...dobJobNumbers, jn];
    setDobJobSaving(true);
    try {
      await api.put(`/dob/project-jobs/${selected.id}`, { dob_job_numbers: updated });
      setDobJobNumbers(updated);
      setNewDobJob('');
      await fetchDobStatuses();
      await fetchProjectFilingOptions();
      toast.success(`DOB job ${jn} added`);
    } catch { toast.error('Failed to add DOB job number'); }
    setDobJobSaving(false);
  };

  const removeDobJobNumber = async (jn) => {
    const updated = dobJobNumbers.filter(j => j !== jn);
    setDobJobSaving(true);
    try {
      await api.put(`/dob/project-jobs/${selected.id}`, { dob_job_numbers: updated });
      setDobJobNumbers(updated);
      await fetchDobStatuses();
      await fetchProjectFilingOptions();
      toast.success(`DOB job ${jn} removed`);
    } catch { toast.error('Failed to remove DOB job number'); }
    setDobJobSaving(false);
  };

  const updateStatus = async (status) => {
    try {
      await api.put(`/projects/${selected.id}`, { status });
      toast.success(`Status updated to ${status}`);
      refreshSelected();
      onProjectsRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating status'); }
  };

  const saveProjectName = async () => {
    if (!projectNameVal.trim()) return;
    await api.put(`/projects/${selected.id}`, { name: projectNameVal });
    toast.success('Project name updated');
    setEditProjectName(false);
    refreshSelected();
    onProjectsRefresh();
  };

  const saveProjectNumber = async () => {
    if (!projectNumberVal.trim()) return;
    await api.put(`/projects/${selected.id}`, { project_number: projectNumberVal });
    toast.success('Project number updated');
    setEditProjectNumber(false);
    refreshSelected();
    onProjectsRefresh();
  };

  const saveStartDate = async () => {
    // Empty string clears the field. Send exactly what the input holds.
    await api.put(`/projects/${selected.id}`, { start_date: startDateVal || null });
    toast.success(startDateVal ? 'Start date updated' : 'Start date cleared');
    setEditStartDate(false);
    refreshSelected();
    onProjectsRefresh();
  };

  const saveAddress = async () => {
    await api.put(`/projects/${selected.id}`, { address: addressVal });
    toast.success(addressVal.trim() ? 'Project address updated' : 'Project address cleared');
    setEditAddress(false);
    refreshSelected();
    onProjectsRefresh();
  };

  const saveBin = async () => {
    const v = (binVal || '').trim();
    if (v && !/^\d{7}$/.test(v)) { toast.error('BIN must be exactly 7 digits'); return; }
    setBinSaving(true);
    try {
      await api.put(`/projects/${selected.id}`, { bin_number: v || null });
      toast.success(v ? 'BIN saved' : 'BIN cleared');
      setEditBin(false);
      // Non-blocking BIN<->address validation (visibility warning only; never blocks).
      try { await api.post(`/projects/${selected.id}/validate-bin-address`); } catch { /* ignore */ }
      refreshSelected();
    } catch { toast.error('Failed to save BIN'); }
    setBinSaving(false);
  };

  const bulkDiscoverDob = async () => {
    setSelectedKeys(new Set());
    setDiscoverResults({ dob_violations: [], dob_filings: [], ecb_violations: [], elevator: [], boiler: [], facade: [], hpd: [] });
    setDiscoverWarnings({});
    setDiscoverOpen(true);
    setDiscovering(true);
    try {
      const r = await api.post(`/projects/${selected.id}/bulk-discover-dob`);
      setDiscoverResults(r.data.groups || { dob_violations: [], dob_filings: [], ecb_violations: [], elevator: [], boiler: [], facade: [], hpd: [] });
      setDiscoverWarnings(r.data.warnings || {});
    } catch { toast.error('NYC Open Data discovery failed'); }
    setDiscovering(false);
  };

  const toggleDiscoverKey = (k) => setSelectedKeys(prev => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const toggleSelectAllGroup = (items, prefix) => setSelectedKeys(prev => {
    const n = new Set(prev);
    const keys = items.map(it => prefix + discoverIdOf(it));
    const allSel = keys.length > 0 && keys.every(k => n.has(k));
    keys.forEach(k => { if (allSel) n.delete(k); else n.add(k); });
    return n;
  });

  const addDiscoverSelected = async () => {
    setAddingSelected(true);
    try {
      // Filings: reuse the EXISTING job-number add path (append to dob_job_numbers).
      const selFilings = (discoverResults.dob_filings || []).filter(f => selectedKeys.has('f:' + f.job_number));
      const newJobs = Array.from(new Set(selFilings.map(f => f.job_number).filter(jn => jn && !dobJobNumbers.includes(jn))));
      if (newJobs.length) {
        const updated = [...dobJobNumbers, ...newJobs];
        await api.put(`/dob/project-jobs/${selected.id}`, { dob_job_numbers: updated });
        setDobJobNumbers(updated);
      }
      // Violations: persist to the project-level violations store (deduped server-side).
      const selViolations = [
        ...(discoverResults.dob_violations || []).filter(v => selectedKeys.has('dv:' + v.violation_number)),
        ...(discoverResults.ecb_violations || []).filter(v => selectedKeys.has('ev:' + v.violation_number)),
      ];
      if (selViolations.length) {
        await api.post(`/projects/${selected.id}/dob-violations`, { violations: selViolations });
      }
      // Elevator / Boiler / Facade: ONE generic path per source (no per-source code).
      const NEW_SOURCE_GROUPS = [
        { key: 'elevator', prefix: 'el:' },
        { key: 'boiler', prefix: 'bo:' },
        { key: 'facade', prefix: 'fa:' },
        { key: 'hpd', prefix: 'hp:' },
      ];
      let extraAdded = 0;
      for (const g of NEW_SOURCE_GROUPS) {
        const sel = (discoverResults[g.key] || []).filter(it => selectedKeys.has(g.prefix + discoverIdOf(it)));
        if (sel.length) {
          const resp = await api.post(`/projects/${selected.id}/dob-records`, { source: g.key, records: sel });
          extraAdded += (resp.data?.added ?? sel.length);
        }
      }
      const total = newJobs.length + selViolations.length + extraAdded;
      if (total === 0) { toast.error('Nothing selected to add'); setAddingSelected(false); return; }
      await fetchDobStatuses();
      await fetchProjectFilingOptions();
      toast.success(`Added ${total} record${total === 1 ? '' : 's'}`);
      setDiscoverOpen(false);
    } catch { toast.error('Failed to add selected records'); }
    setAddingSelected(false);
  };

  const updateSectionStatus = async (sectionId, status) => {
    await api.put(`/contract-sections/${sectionId}`, { status });
    toast.success('Section status updated');
    refreshSelected();
  };

  const openEditProjectSection = (s) => {
    setEditProjectSection(s);
    setEditProjSectionForm({
      status: s.status || 'Not Started',
      source_tag: s.source_tag || 'Original Contract',
      reference_po_number: s.reference_po_number || '',
      approval_date: s.approval_date || '',
      expiration_date: s.expiration_date || '',
      dob_filing_number: s.dob_filing_number || '',
      notes: s.notes || '',
    });
    setProjSectionEventTypePrompt(false);
  };

  const saveProjectSection = async () => {
    const prevExpDate = editProjectSection.expiration_date || '';
    const newExpDate = editProjSectionForm.expiration_date || '';
    const prevFiling = editProjectSection.dob_filing_number || '';
    const newFiling = (editProjSectionForm.dob_filing_number || '').trim();

    // Save section fields (including dob_filing_number)
    const sectionFields = { ...editProjSectionForm };
    await api.put(`/contract-sections/${editProjectSection.id}`, sectionFields);

    // Link filing number if changed
    if (newFiling !== prevFiling) {
      try {
        if (newFiling) {
          await api.post('/dob/link-section', { section_id: editProjectSection.id, filing_number: newFiling });
        } else {
          await api.post('/dob/link-section', { section_id: editProjectSection.id });
        }
        toast.success(newFiling ? `Filing ${newFiling} linked` : 'Filing number cleared');
      } catch { toast.error('Failed to link filing number'); }
    }

    toast.success('Section updated');
    if (newExpDate && newExpDate !== prevExpDate) {
      setProjSectionEventTypePrompt(true);
      return;
    }
    setEditProjectSection(null);
    refreshSelected();
  };

  const confirmProjSectionEventType = async () => {
    try {
      await api.post('/calendar/events', {
        name: `${editProjectSection.section_name} - ${projSectionPendingEventType}`,
        event_type: projSectionPendingEventType,
        date: editProjSectionForm.expiration_date,
        all_day: true,
        linked_project: selected.id,
        linked_section: editProjectSection.id,
      });
      toast.success('Calendar event created');
    } catch (e) { toast.error('Event creation failed'); }
    setProjSectionEventTypePrompt(false);
    setEditProjectSection(null);
    refreshSelected();
  };

  const skipProjSectionEventType = () => {
    setProjSectionEventTypePrompt(false);
    setEditProjectSection(null);
    refreshSelected();
  };

  const createTodo = async () => {
    if (!todoForm.title.trim()) return toast.error('Title required');
    if (!todoForm.contract_section_id) return toast.error('Section is required when linking a To-Do to a project');
    try {
      await api.post('/tasks', { ...todoForm, project_id: selected.id });
      toast.success('To-Do created');
      setTodoFormOpen(false);
      setTodoForm({ title: '', description: '', contract_section_id: '', assigned_to: '', priority: 'Medium', due_date: '' });
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating To-Do'); }
  };

  const openEditTodo = (t) => {
    setEditTodo(t);
    setEditTodoForm({ title: t.title, description: t.description || '', contract_section_id: t.contract_section_id || '', assigned_to: t.assigned_to || '', priority: t.priority || 'Medium', status: t.status || 'Not Started', due_date: t.due_date || '' });
    setTodoFormOpen(false);
  };

  const saveEditTodo = async () => {
    if (!editTodoForm.title.trim()) return toast.error('Title required');
    if (!editTodoForm.contract_section_id) return toast.error('Section is required');
    try {
      await api.put(`/tasks/${editTodo.id}`, editTodoForm);
      toast.success('To-Do updated');
      setEditTodo(null);
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating To-Do'); }
  };

  const deleteTodo = async (taskId) => {
    if (!window.confirm('Delete this To-Do?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('To-Do deleted');
      setEditTodo(null);
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting To-Do'); }
  };

  const createTodoFromProjectReminder = (r) => {
    setTodoFormOpen(true);
    setTodoForm({ title: `Action: ${r.action_items?.substring(0, 50)}`, description: r.action_items || '', contract_section_id: '', assigned_to: '', priority: 'Medium', due_date: '' });
  };

  const dismissProjectReminder = async (reminderId) => {
    try {
      await api.put(`/action-item-reminders/${reminderId}/dismiss`);
      setProjectReminders(prev => prev.filter(r => r.id !== reminderId));
    } catch { /* ignore */ }
  };

  const dismissAllProjectReminders = async () => {
    if (projectReminders.length === 0) return;
    try {
      await api.put(`/action-item-reminders/dismiss-all?project_id=${selected.id}`);
      setProjectReminders([]);
      toast.success('All action items cleared');
    } catch { /* ignore */ }
  };

  const addStatusEntry = async (alsoLogTime = false) => {
    if (!statusText.trim()) return toast.error('Status text required');
    const r = await api.post(`/projects/${selected.id}/status`, { status_text: statusText, action_items: actionItems });
    toast.success('Status entry added');
    // Capture the typed text BEFORE we reset so the QuickLogTimeSheet can
    // open with it already pre-filled (HTML stripped to plain notes).
    const carriedNotes = (() => {
      const tmp = document.createElement('div');
      tmp.innerHTML = statusText;
      return (tmp.textContent || tmp.innerText || '').trim();
    })();
    const newStatusId = r?.data?.id || null;
    setStatusText('');
    setActionItems('');
    refreshSelected();
    if (alsoLogTime) {
      setQuickLogNotes(carriedNotes);
      setQuickLogStatusId(newStatusId);
      setQuickLogSurface('status_log_log_time');
      setQuickLogOpen(true);
    }
  };

  const openEditStatusEntry = (s) => {
    setEditStatusEntry(s);
    setEditStatusForm({ status_text: s.status_text || '', action_items: s.action_items || '' });
  };

  const saveEditStatusEntry = async () => {
    if (!editStatusForm.status_text.trim()) return toast.error('Status text required');
    try {
      await api.put(`/projects/${selected.id}/status/${editStatusEntry.id}`, editStatusForm);
      toast.success('Status entry updated');
      setEditStatusEntry(null);
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating status'); }
  };

  const handleMeetingNoteSaved = () => {
    setMeetingNoteFormOpen(false);
    refreshSelected();
  };

  const handleMeetingNoteLogTime = (carriedNotes) => {
    setQuickLogNotes(carriedNotes);
    // Meeting notes are not tied to a running-status entry, so clear any
    // stale status-log id from a previous flow and mark the surface.
    setQuickLogStatusId(null);
    setQuickLogSurface('meeting_note_log_time');
    setQuickLogOpen(true);
  };

  const openEditMeetingNote = (n) => {
    setEditMeetingNote(n);
    setMeetingNoteFormOpen(false);
  };

  const handleMeetingNoteEdited = () => {
    setEditMeetingNote(null);
    refreshSelected();
  };

  const deleteMeetingNote = async (noteId) => {
    if (!window.confirm('Delete this meeting note?')) return;
    await api.delete(`/projects/${selected.id}/meeting-notes/${noteId}`);
    toast.success('Meeting note deleted');
    refreshSelected();
  };

  const todosBySection = {};
  for (const t of (selected?.tasks || [])) {
    const secId = t.contract_section_id || '_general';
    if (!todosBySection[secId]) todosBySection[secId] = [];
    todosBySection[secId].push(t);
  }
  // Within each section: incomplete to-dos first (closest due date first),
  // then completed to-dos last (also chronological). To-dos with no due
  // date are treated as far-future so they naturally settle at the bottom
  // of their bucket without displacing dated items.
  for (const secId of Object.keys(todosBySection)) {
    todosBySection[secId].sort((a, b) => {
      const aDone = a.status === 'Completed';
      const bDone = b.status === 'Completed';
      if (aDone !== bDone) return aDone ? 1 : -1;
      const aDue = a.due_date || '9999-12-31';
      const bDue = b.due_date || '9999-12-31';
      return aDue.localeCompare(bDue);
    });
  }

  const getSectionLabel = (secId) => {
    if (secId === '_general') return 'General';
    const sec = (selected?.sections || []).find(s => s.id === secId);
    return sec ? `${sec.section_number ? sec.section_number + ' - ' : ''}${sec.section_name}` : 'Unknown Section';
  };

  return (
    <div className="animate-fade-in pb-10" data-testid="project-detail-page">
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3" data-testid="project-detail-breadcrumb">
        <button type="button" onClick={onBack} className="hover:text-primary transition-colors" data-testid="breadcrumb-projects-link">Projects</button>
        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        <span className="text-foreground font-medium truncate max-w-[60vw]" data-testid="breadcrumb-project-name">{selected?.name}</span>
      </nav>
      <Card className="border-border bg-card shadow-none mb-4" data-testid="project-detail-header-card">
        <CardContent className="p-5 space-y-3">
          {/* Record ID — 13px DM Mono muted, sits above the title */}
          {editProjectNumber ? (
            <div className="flex items-center gap-1">
              <Input value={projectNumberVal} onChange={e => setProjectNumberVal(e.target.value)} className="h-7 w-44 bg-background text-sm font-mono" data-testid="edit-project-number-input" autoFocus onKeyDown={e => { if (e.key === 'Enter') saveProjectNumber(); if (e.key === 'Escape') setEditProjectNumber(false); }} />
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={saveProjectNumber} data-testid="save-project-number-btn"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /></Button>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditProjectNumber(false)}><X className="h-3.5 w-3.5" /></Button>
            </div>
          ) : (
            <p className="text-sm font-mono text-muted-foreground cursor-pointer hover:text-primary transition-colors group w-fit"
               onClick={() => { setProjectNumberVal(selected?.project_number || ''); setEditProjectNumber(true); setEditProjectName(false); }}
               data-testid="project-number-editable">
              {selected?.project_number}
              <Pencil className="h-3 w-3 inline ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
          )}
          {/* Title — 18px weight 500 */}
          <h1 className="text-2xl font-semibold leading-tight tracking-tight" data-testid="project-detail-title">
            {editProjectName ? (
              <span className="flex items-center gap-1">
                <Input value={projectNameVal} onChange={e => setProjectNameVal(e.target.value)} className="h-8 w-72 bg-background text-[18px] font-medium" data-testid="edit-project-name-input" autoFocus onKeyDown={e => { if (e.key === 'Enter') saveProjectName(); if (e.key === 'Escape') setEditProjectName(false); }} />
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={saveProjectName} data-testid="save-project-name-btn"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditProjectName(false)}><X className="h-3.5 w-3.5" /></Button>
              </span>
            ) : (
              <span className="cursor-pointer hover:text-primary transition-colors group inline-flex items-center"
                    onClick={() => { setProjectNameVal(selected?.name || ''); setEditProjectName(true); setEditProjectNumber(false); }}
                    data-testid="project-name-editable">
                {selected?.name}
                <Pencil className="h-3 w-3 inline ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
              </span>
            )}
          </h1>
          {/* Metadata — 13px muted */}
          <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>
              <RouterLink to="/clients" className="text-primary hover:underline" data-testid="project-client-link">{selected?.client_name}</RouterLink>
              {selected?.address ? ` · ${selected.address}` : ''}
            </span>
            <MissingFieldsAlert
              fields={[
                { label: 'Project Manager', present: !!(selected?.project_manager_ids?.length) },
                { label: 'Start Date', present: !!selected?.start_date },
                { label: 'Project Address', present: !!selected?.address?.trim() },
              ]}
              testId="project-missing-fields"
            />
          </div>

          {/* Editable Start Date + Project Address row. Both fields are
              flagged as "missing" in the alert above when empty, so we
              expose inline editors here to let users fix them without
              leaving the panel. */}
          <div className="flex items-start gap-4 flex-wrap text-xs pt-1" data-testid="project-editable-meta-row">
            {/* Start Date */}
            <div className="flex items-center gap-1.5">
              <span className="uppercase tracking-wider text-muted-foreground font-medium">Start Date:</span>
              {editStartDate ? (
                <div className="flex items-center gap-1">
                  <Input
                    type="date"
                    value={startDateVal}
                    onChange={e => setStartDateVal(e.target.value)}
                    className="h-7 w-40 bg-background border-border text-xs"
                    data-testid="edit-start-date-input"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') saveStartDate(); if (e.key === 'Escape') setEditStartDate(false); }}
                  />
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={saveStartDate} data-testid="save-start-date-btn">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditStartDate(false)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { setStartDateVal(selected?.start_date || ''); setEditStartDate(true); setEditAddress(false); }}
                  className={`inline-flex items-center gap-1 group cursor-pointer transition-colors ${
                    selected?.start_date ? 'text-foreground hover:text-primary' : 'text-amber-400 hover:text-amber-300 italic'
                  }`}
                  data-testid="start-date-editable"
                >
                  {selected?.start_date || 'Not set'}
                  <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              )}
            </div>

            {/* Project Address */}
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <span className="uppercase tracking-wider text-muted-foreground font-medium shrink-0">Address:</span>
              {editAddress ? (
                <div className="flex items-center gap-1 flex-1">
                  <Input
                    value={addressVal}
                    onChange={e => setAddressVal(e.target.value)}
                    placeholder="Project address"
                    className="h-7 bg-background border-border text-xs flex-1 min-w-0"
                    data-testid="edit-address-input"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') saveAddress(); if (e.key === 'Escape') setEditAddress(false); }}
                  />
                  <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={saveAddress} data-testid="save-address-btn">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => setEditAddress(false)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { setAddressVal(selected?.address || ''); setEditAddress(true); setEditStartDate(false); }}
                  className={`inline-flex items-center gap-1 group cursor-pointer transition-colors min-w-0 truncate ${
                    selected?.address?.trim() ? 'text-foreground hover:text-primary' : 'text-amber-400 hover:text-amber-300 italic'
                  }`}
                  data-testid="address-editable"
                >
                  <span className="truncate">{selected?.address?.trim() || 'Not set'}</span>
                  <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </button>
              )}
            </div>
          </div>
          {selected?.contract_id && (
            <RouterLink
              to={`/contracts?contract=${selected.contract_id}`}
              className="text-sm text-primary hover:underline inline-flex items-center gap-1 w-fit"
              data-testid="project-view-contract-link"
            >
              <ExternalLink className="h-3 w-3" /> View linked contract
            </RouterLink>
          )}
          {selected && (
          <div className="flex flex-col gap-3 pt-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${statusColors[selected.status] || ''}`}>{selected.status}</Badge>
              <Select value={selected.status} onValueChange={updateStatus}>
                <SelectTrigger className="w-44 h-8 bg-background border-border text-xs" aria-label="Project status" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{PROJECT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {selected.status !== 'Archived' && (
                <Button
                  size="sm"
                  variant={actionHourEntryId ? "outline" : "default"}
                  disabled={actionHourLoading}
                  onClick={toggleActionHour}
                  className={actionHourEntryId
                    ? "text-xs border-amber-500/30 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 ml-auto"
                    : "text-xs bg-amber-600 hover:bg-amber-700 text-white ml-auto"}
                  data-testid="action-hour-toggle-btn"
                >
                  <Zap className="h-3 w-3 mr-1" />
                  {actionHourLoading ? '...' : actionHourEntryId ? 'On Action Hour' : 'Add to Action Hour'}
                </Button>
              )}
            </div>

            {/* Project Manager(s) - inline assign/edit. Mirrors Contracts page UX. */}
            <div className="flex items-start gap-2 flex-wrap" data-testid="project-pm-row">
              <div className="flex items-center gap-1 mt-1.5 shrink-0">
                <User className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Project Manager(s)</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {(() => {
                  const pmIds = selected.project_manager_ids || [];
                  if (pmIds.length === 0 && !pmEditing) {
                    return (
                      <span className="text-xs text-muted-foreground italic mt-1">None assigned</span>
                    );
                  }
                  const userMap = Object.fromEntries((users || []).map(u => [u.id, u]));
                  return pmIds.map(pid => {
                    const u = userMap[pid];
                    return (
                      <Badge key={pid} variant="outline" className="text-xs gap-1 pr-1 border-primary/30" data-testid={`project-pm-badge-${pid}`}>
                        {u?.name || 'Unknown'}
                        {pmEditing && (
                          <button
                            onClick={() => removeProjectManager(pid)}
                            className="ml-0.5 hover:text-destructive"
                            data-testid={`project-pm-remove-${pid}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    );
                  });
                })()}
                {pmEditing && (
                  <Select value="" onValueChange={addProjectManager}>
                    <SelectTrigger className="h-7 w-44 bg-background border-border text-xs" data-testid="project-pm-add-select">
                      <SelectValue placeholder="+ Add PM..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {(users || [])
                        .filter(u => u.role !== 'Client' && !((selected.project_manager_ids) || []).includes(u.id))
                        .map(u => (
                          <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setPmEditing(v => !v)}
                  data-testid="project-pm-edit-toggle"
                >
                  {pmEditing ? <><Check className="h-3 w-3 mr-1" /> Done</> : <><Pencil className="h-3 w-3 mr-1" /> Edit</>}
                </Button>
              </div>
            </div>

            {/* Jurisdiction - carried from the contract at creation; freely editable here. */}
            <div className="flex items-start gap-2 flex-wrap mt-2" data-testid="project-jurisdiction-row">
              <div className="flex items-center gap-1 mt-1.5 shrink-0">
                <Landmark className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Jurisdiction</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                {PROJECT_JURISDICTION_OPTIONS.map(opt => {
                  const sel = (Array.isArray(selected.jurisdiction) ? selected.jurisdiction : []).includes(opt.value);
                  return (
                    <button key={opt.value} type="button" onClick={() => toggleProjectJurisdiction(opt.value)}
                      className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-primary/50'}`}
                      data-testid={`project-jurisdiction-${opt.value}`}>
                      {opt.label}
                    </button>
                  );
                })}
                {(Array.isArray(selected.jurisdiction) ? selected.jurisdiction : []).includes('other') && (
                  <Input value={projJurOther} onChange={e => setProjJurOther(e.target.value)} onBlur={e => saveProjectJurisdictionOther(e.target.value)} placeholder="Specify other jurisdiction" className="h-7 w-48 bg-background border-border text-xs" data-testid="project-jurisdiction-other" />
                )}
                {(!Array.isArray(selected.jurisdiction) && typeof selected.jurisdiction === 'string' && selected.jurisdiction.trim()) && (
                  <span className="text-xs text-muted-foreground italic" data-testid="project-jurisdiction-legacy">Current: {selected.jurisdiction} (select to update)</span>
                )}
              </div>
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      {selected && (
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList>
                <TabsTrigger value="sections" className="text-xs"><FileText className="h-3 w-3 mr-1" /> Sections</TabsTrigger>
                <TabsTrigger value="building-profile" className="text-xs" data-testid="tab-building-profile"><Building2 className="h-3 w-3 mr-1" /> Building/Violations Profile</TabsTrigger>
                <TabsTrigger value="todos" className="text-xs"><ListTodo className="h-3 w-3 mr-1" /> To-Dos</TabsTrigger>
                <TabsTrigger value="status" className="text-xs"><Clock className="h-3 w-3 mr-1" /> Status Log</TabsTrigger>
                <TabsTrigger value="meeting-notes" className="text-xs" data-testid="tab-meeting-notes"><MessageSquareText className="h-3 w-3 mr-1" /> Meeting Notes</TabsTrigger>
                <TabsTrigger value="docs" className="text-xs"><Paperclip className="h-3 w-3 mr-1" /> Attachments{projectAttachments.length > 0 ? ` (${projectAttachments.length})` : ''}</TabsTrigger>
              </TabsList>

              {/* Sections */}
              <TabsContent value="sections" className="mt-3 space-y-2">
                {/* DOB Job Numbers Management */}
                <Card className="border-border bg-card/50 shadow-none" data-testid="dob-job-numbers-card">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        <p className="text-xs font-semibold text-muted-foreground">DOB Job Numbers</p>
                      </div>
                      {dobJobNumbers.length > 0 && (
                        <span className="text-xs text-muted-foreground">{(dobStatuses.filings || []).length} filing(s) tracked</span>
                      )}
                    </div>
                    {dobJobNumbers.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {dobJobNumbers.map(jn => (
                          <Badge key={jn} variant="outline" className="text-xs font-mono gap-1 pr-1 border-primary/30" data-testid={`dob-job-badge-${jn}`}>
                            {jn}
                            <button onClick={() => removeDobJobNumber(jn)} className="ml-0.5 hover:text-destructive" disabled={dobJobSaving}><X className="h-3 w-3" /></button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-1.5">
                      <Input
                        placeholder="Add DOB job number (e.g. B00248426)"
                        value={newDobJob}
                        onChange={e => setNewDobJob(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addDobJobNumber()}
                        className="bg-background border-border font-mono text-xs h-7 flex-1"
                        data-testid="dob-add-job-input"
                      />
                      <Button size="sm" onClick={addDobJobNumber} disabled={dobJobSaving || !newDobJob.trim()} className="h-7 text-xs px-2 bg-primary text-primary-foreground" data-testid="dob-add-job-btn">
                        <Plus className="h-3 w-3 mr-0.5" /> Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Source Tag P.O. Numbers */}
                {(() => {
                  const tags = [...new Set((selected.sections || []).map(s => s.source_tag || 'Original Contract'))];
                  if (tags.length === 0) return null;
                  return (
                    <SourceTagPoCard
                      projectId={selected.id}
                      tags={tags}
                      initialPos={selected.source_tag_pos || {}}
                    />
                  );
                })()}

                {/* DOB Filing Timeline */}
                {(dobStatuses.filings || []).length > 0 && (
                  <Card className="border-border bg-card/50 shadow-none overflow-hidden" data-testid="dob-filing-timeline">
                    <button
                      className="w-full flex items-center justify-between p-3 hover:bg-secondary/30 transition-colors text-left"
                      onClick={() => setTimelineExpanded(!timelineExpanded)}
                      data-testid="dob-timeline-toggle"
                    >
                      <div className="flex items-center gap-1.5">
                        {timelineExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                        <Clock className="h-3.5 w-3.5 text-primary" />
                        <span className="text-xs font-semibold text-muted-foreground">Filing Timeline</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{(dobStatuses.filings || []).length} filings</span>
                    </button>
                    {timelineExpanded && (
                      <div className="px-3 pb-3">
                        <div className="relative ml-3 border-l-2 border-border pl-4 space-y-0">
                          {[...(dobStatuses.filings || [])]
                            .sort((a, b) => {
                              const da = a.filing_date || a.permit_issued_date || '';
                              const db = b.filing_date || b.permit_issued_date || '';
                              return db.localeCompare(da);
                            })
                            .map((f, i) => {
                              const statusCat = _getTimelineCategory(f.filing_status);
                              return (
                                <div key={i} className="relative pb-3 last:pb-0" data-testid={`timeline-item-${i}`}>
                                  <div className={`absolute -left-[22px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background ${statusCat.dot}`} />
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <Badge variant="outline" className={`text-xs border ${statusCat.badge}`}>{f.filing_status}</Badge>
                                        <span className="text-xs font-mono text-muted-foreground">{f.job_filing_number || f.job_number}</span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                        <span className="text-xs text-muted-foreground">{f.dataset_short}</span>
                                        {f.address && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{f.address}</span>}
                                      </div>
                                    </div>
                                    <div className="text-right shrink-0 space-y-0.5">
                                      {f.filing_date && <p className="text-xs text-muted-foreground">{f.filing_date}</p>}
                                      {f.permit_issued_date && <p className="text-xs text-emerald-400">Issued: {f.permit_issued_date}</p>}
                                      {f.permit_expiration_date && <p className="text-xs text-amber-400">Exp: {f.permit_expiration_date}</p>}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                        {dobStatuses.last_checked && (
                          <p className="text-xs text-muted-foreground mt-2 text-right">Last checked: {new Date(dobStatuses.last_checked).toLocaleString()}</p>
                        )}
                      </div>
                    )}
                  </Card>
                )}

                {editProjectSection && (
                  <Card className="border-primary/30 border-dashed bg-primary/5 shadow-none" data-testid="project-edit-section-form">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-primary">Edit Section: {editProjectSection.section_name}</p>
                        <Badge variant="outline" className="text-xs">{editProjectSection.section_origin || 'N/A'}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Status</Label>
                          <Select value={editProjSectionForm.status} onValueChange={v => setEditProjSectionForm({ ...editProjSectionForm, status: v })}>
                            <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-card border-border">{SECTION_STATUSES.map(st => <SelectItem key={st} value={st} className="text-xs">{st}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Source Tag</Label>
                          <Select value={editProjSectionForm.source_tag || 'Original Contract'} onValueChange={v => setEditProjSectionForm({ ...editProjSectionForm, source_tag: v })}>
                            <SelectTrigger className="bg-background border-border" data-testid="proj-section-source-tag"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-card border-border">{SOURCE_TAG_OPTIONS.map(t => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">PO / Reference Number</Label>
                          <Input placeholder="PO or reference #" value={editProjSectionForm.reference_po_number} onChange={e => setEditProjSectionForm({ ...editProjSectionForm, reference_po_number: e.target.value })} className="bg-background border-border" data-testid="proj-section-po" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Approval Date</Label>
                          <Input type="date" value={editProjSectionForm.approval_date || ''} onChange={e => setEditProjSectionForm({ ...editProjSectionForm, approval_date: e.target.value })} className="bg-background border-border" data-testid="proj-section-approval-date" />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Expiration Date</Label>
                          <Input type="date" value={editProjSectionForm.expiration_date || ''} onChange={e => setEditProjSectionForm({ ...editProjSectionForm, expiration_date: e.target.value })} className="bg-background border-border" data-testid="proj-section-expiration" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">DOB Filing Number</Label>
                        {projectFilingOptions.length > 0 ? (
                          <Select value={editProjSectionForm.dob_filing_number || '_none_'} onValueChange={v => setEditProjSectionForm({ ...editProjSectionForm, dob_filing_number: v === '_none_' ? '' : v })}>
                            <SelectTrigger className="bg-background border-border font-mono text-xs" data-testid="proj-section-dob-filing"><SelectValue placeholder="Select filing..." /></SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="_none_" className="text-xs text-muted-foreground">None</SelectItem>
                              {projectFilingOptions.map(o => (
                                <SelectItem key={o.filing_number} value={o.filing_number} className="text-xs font-mono">
                                  {o.filing_number} {o.suffix && <span className="text-muted-foreground">({o.suffix})</span>} · {o.filing_status}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input placeholder={dobJobNumbers.length > 0 ? "No filings found for project jobs" : "Add DOB Job Numbers first"} value={editProjSectionForm.dob_filing_number || ''} onChange={e => setEditProjSectionForm({ ...editProjSectionForm, dob_filing_number: e.target.value })} className="bg-background border-border font-mono text-xs" data-testid="proj-section-dob-filing" disabled={dobJobNumbers.length === 0} />
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Scope of Work / Description</Label>
                        <div className="mt-1" data-testid="proj-section-notes-editor">
                          <RichTextEditor
                            value={editProjSectionForm.notes || ''}
                            onChange={v => setEditProjSectionForm({ ...editProjSectionForm, notes: v })}
                            placeholder="Scope of work description carried over from the contract..."
                            collapsedHeight={100}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveProjectSection()} className="bg-primary text-primary-foreground" data-testid="save-proj-section-btn">Save Changes</Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditProjectSection(null); setProjSectionEventTypePrompt(false); }}>Cancel</Button>
                      </div>
                      {projSectionEventTypePrompt && (
                        <Card className="border-amber-500/30 bg-amber-500/5 shadow-none mt-2">
                          <CardContent className="p-3 space-y-2">
                            <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-amber-400" /><p className="text-xs font-semibold text-amber-400">Calendar Event Type</p></div>
                            <p className="text-xs text-muted-foreground">A calendar event will be created for this expiration date. Select the event type:</p>
                            <Select value={projSectionPendingEventType} onValueChange={setProjSectionPendingEventType}>
                              <SelectTrigger className="bg-background border-border" data-testid="proj-expiration-event-type-select"><SelectValue placeholder="Select event type" /></SelectTrigger>
                              <SelectContent className="bg-card border-border">{SECTION_EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={confirmProjSectionEventType} className="bg-amber-500 text-white hover:bg-amber-600" data-testid="proj-confirm-event-type-btn"><CalendarDays className="h-3 w-3 mr-1" /> Save & Create Event</Button>
                              <Button size="sm" variant="outline" onClick={skipProjSectionEventType} className="text-xs text-muted-foreground">Skip (set later)</Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </CardContent>
                  </Card>
                )}

                {groupSectionsByTag(selected.sections || []).map(group => (
                  <div key={group.tag} className="mb-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 px-1">{group.tag}</p>
                    {group.sections.map(s => {
                      const filings = dobStatuses.filings || [];
                      const sectionFiling = s.dob_filing_number || s.dob_job_number;
                      const matchedFilings = sectionFiling
                        ? filings.filter(f => f.job_filing_number === sectionFiling || f.job_number === sectionFiling || (f.job_filing_number || '').startsWith(sectionFiling))
                        : [];
                      const primaryFiling = matchedFilings[0];
                      return (
                      <Card key={s.id} className="border-border bg-background shadow-none cursor-pointer hover:border-primary/30 transition-colors mb-2" onClick={() => openEditProjectSection(s)} data-testid={`project-section-${s.id}`}>
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {s.section_number && <Badge variant="outline" className="text-xs font-mono">{s.section_number}</Badge>}
                                <p className="text-sm font-medium">{s.section_name}</p>
                                {s.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                <p className="text-xs text-muted-foreground">{s.category} {s.agency && `| ${s.agency}`}</p>
                                {s.reference_po_number && <span className="text-xs text-muted-foreground">PO: {s.reference_po_number}</span>}
                                {s.approval_date && <span className="text-xs text-muted-foreground">Approved: {s.approval_date}</span>}
                                {s.expiration_date && <span className="text-xs text-amber-400">Exp: {s.expiration_date}</span>}
                              </div>
                              {s.notes && (
                                <div
                                  className="mt-1.5 text-xs overflow-hidden"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`project-section-notes-${s.id}`}
                                >
                                  <RichTextDisplay value={s.notes} collapsedHeight={40} />
                                </div>
                              )}
                              {primaryFiling && (
                                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div
                                          className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                                          onClick={(e) => { e.stopPropagation(); openDobNowJobSummary(primaryFiling.job_filing_number || primaryFiling.job_number || sectionFiling); }}
                                          data-testid={`dob-tag-link-${s.id}`}
                                        >
                                          <Building2 className="h-3 w-3 text-muted-foreground shrink-0" />
                                          <Badge variant="outline" className={`text-xs border ${getDobStatusColor(primaryFiling.filing_status)}`} data-testid={`dob-status-${s.id}`}>
                                            {primaryFiling.filing_status}
                                          </Badge>
                                          <span className="text-xs text-muted-foreground font-mono">{sectionFiling}</span>
                                          {primaryFiling.permit_expiration_date && <span className="text-xs text-amber-400">Exp: {primaryFiling.permit_expiration_date}</span>}
                                          <ExternalLink className="h-2.5 w-2.5 text-muted-foreground/70 shrink-0" />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="bg-popover text-popover-foreground border-border max-w-xs p-3 shadow-lg">
                                        <div className="space-y-1 text-xs">
                                          <p className="font-semibold">{primaryFiling.dataset_short}</p>
                                          <p>Status: <span className="font-medium">{primaryFiling.filing_status}</span></p>
                                          {primaryFiling.permit_issued_date && <p>Permit Issued: {primaryFiling.permit_issued_date}</p>}
                                          {primaryFiling.permit_expiration_date && <p>Permit Expires: <span className="text-amber-400">{primaryFiling.permit_expiration_date}</span></p>}
                                          {primaryFiling.address && <p>Address: {primaryFiling.address}</p>}
                                          {matchedFilings.length > 1 && <p className="text-muted-foreground">{matchedFilings.length} filings across datasets</p>}
                                          {dobStatuses.last_checked && <p className="text-muted-foreground">Checked: {new Date(dobStatuses.last_checked).toLocaleString()}</p>}
                                          <p className="text-primary mt-1 italic">Click to open on DOBNOW →</p>
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  {matchedFilings.length > 1 && matchedFilings.slice(1).map((f, i) => (
                                    <TooltipProvider key={i}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Badge
                                            variant="outline"
                                            className={`text-xs border cursor-pointer hover:opacity-80 transition-opacity ${getDobStatusColor(f.filing_status)}`}
                                            onClick={(e) => { e.stopPropagation(); openDobNowJobSummary(f.job_filing_number || f.job_number); }}
                                            data-testid={`dob-extra-tag-${s.id}-${i}`}
                                          >
                                            {f.dataset_short}: {f.filing_status}
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="bg-popover text-popover-foreground border-border max-w-xs p-3 shadow-lg">
                                          <div className="space-y-1 text-xs">
                                            <p className="font-semibold">{f.dataset_name}</p>
                                            <p>Filing: {f.job_filing_number}</p>
                                            <p>Status: {f.filing_status}</p>
                                            {f.permit_issued_date && <p>Permit Issued: {f.permit_issued_date}</p>}
                                            {f.permit_expiration_date && <p>Permit Expires: <span className="text-amber-400">{f.permit_expiration_date}</span></p>}
                                            <p className="text-primary mt-1 italic">Click to open on DOBNOW →</p>
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ))}
                                </div>
                              )}
                              {sectionFiling && !primaryFiling && (
                                <div
                                  className="flex items-center gap-1.5 mt-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => { e.stopPropagation(); openDobNowJobSummary(sectionFiling); }}
                                  data-testid={`dob-fallback-link-${s.id}`}
                                >
                                  <Building2 className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground font-mono">{sectionFiling}</span>
                                  <span className="text-xs text-muted-foreground italic">No filings found · open in DOBNOW</span>
                                  <ExternalLink className="h-2.5 w-2.5 text-muted-foreground/70" />
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Select value={s.status} onValueChange={v => { updateSectionStatus(s.id, v); }} onClick={e => e.stopPropagation()}>
                                <SelectTrigger className="w-28 h-7 bg-transparent border-border text-xs" aria-label="Section status" data-testid={`section-status-${s.id}`} onClick={e => e.stopPropagation()}><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-card border-border">{SECTION_STATUSES.map(st => <SelectItem key={st} value={st} className="text-xs">{st}</SelectItem>)}</SelectContent>
                              </Select>
                              <Pencil className="h-3 w-3 text-muted-foreground" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      );
                    })}
                  </div>
                ))}
                {dobJobNumbers.length > 0 && (
                  <div className="flex justify-end">
                    <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); refreshDobProject(); }} disabled={dobRefreshing} className="text-xs text-muted-foreground hover:text-foreground h-6 px-2" data-testid="refresh-dob-btn">
                      <RefreshCw className={`h-3 w-3 mr-1 ${dobRefreshing ? 'animate-spin' : ''}`} /> {dobRefreshing ? 'Refreshing DOB...' : 'Refresh DOB Statuses'}
                    </Button>
                  </div>
                )}
                {(selected.sections || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No sections linked</p>}
              </TabsContent>

              {/* Building/Violations Profile - consolidated, project-wide DOB view.
                  Reuses the SAME dobStatuses data (fetched once by fetchDobStatuses),
                  the SAME dobJobNumbers array, the SAME _getTimelineCategory logic and
                  refreshDobProject action as the Sections tab. No new fetch path. DOB only. */}
              <TabsContent value="building-profile" className="mt-3 space-y-3" data-testid="building-profile-tab">
                {/* BIN + bulk discovery controls */}
                <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="bp-bin-row">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="uppercase tracking-wider text-muted-foreground font-medium">BIN:</span>
                    {editBin ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={binVal}
                          onChange={e => setBinVal(e.target.value.replace(/[^0-9]/g, '').slice(0, 7))}
                          placeholder="7-digit BIN"
                          className="h-7 w-32 bg-background border-border text-xs"
                          data-testid="edit-bin-input"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') saveBin(); if (e.key === 'Escape') setEditBin(false); }}
                        />
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={saveBin} disabled={binSaving} data-testid="save-bin-btn">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditBin(false)}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setBinVal(selected?.bin_number || ''); setEditBin(true); }}
                        className={`inline-flex items-center gap-1 group cursor-pointer transition-colors ${selected?.bin_number ? 'text-foreground hover:text-primary' : 'text-amber-400 hover:text-amber-300 italic'}`}
                        data-testid="bin-editable"
                      >
                        {selected?.bin_number || 'Not set'}
                        <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    )}
                    {!editBin && selected?.bin_number && selected?.bin_address_validation && selected.bin_address_validation.status !== 'match' && (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-400/90 border border-amber-500/30 rounded px-1.5 py-0.5" data-testid="bin-address-warning">
                        <AlertCircle className="h-3 w-3 shrink-0" /> BIN may not match address on file, verify before relying on this data
                      </span>
                    )}
                  </div>
                  {selected?.bin_number && (
                    <Button size="sm" onClick={bulkDiscoverDob} className="h-7 text-xs px-2.5 bg-primary text-primary-foreground" data-testid="bp-pull-open-data-btn">
                      <Download className="h-3 w-3 mr-1" /> Pull NYC Open Data
                    </Button>
                  )}
                </div>
                {(() => {
                  const filings = dobStatuses.filings || [];
                  const jobs = dobJobNumbers || [];
                  const signedOffCount = filings.filter(f => _getTimelineCategory(f.filing_status) === TIMELINE_CATEGORIES.completed).length;
                  const activeCount = filings.length - signedOffCount;
                  const matchedJobs = new Set(filings.map(f => f.job_number));
                  const noMatchCount = jobs.filter(jn => !matchedJobs.has(jn)).length;
                  return (
                    <>
                      {/* Summary strip */}
                      <div className="grid grid-cols-3 gap-2" data-testid="building-profile-summary">
                        <Card className="border-border bg-card/50 shadow-none">
                          <CardContent className="p-3 text-center">
                            <p className="text-2xl font-semibold tabular-nums text-emerald-400" data-testid="bp-active-count">{activeCount}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Active Filings</p>
                          </CardContent>
                        </Card>
                        <Card className="border-border bg-card/50 shadow-none">
                          <CardContent className="p-3 text-center">
                            <p className="text-2xl font-semibold tabular-nums text-teal-400" data-testid="bp-signedoff-count">{signedOffCount}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Signed Off</p>
                          </CardContent>
                        </Card>
                        <Card className="border-border bg-card/50 shadow-none">
                          <CardContent className="p-3 text-center">
                            <p className="text-2xl font-semibold tabular-nums text-muted-foreground" data-testid="bp-nomatch-count">{noMatchCount}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">No Open Data Match</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Full Filing Timeline - same appearance/behavior as Sections */}
                      {filings.length > 0 ? (
                        <Card className="border-border bg-card/50 shadow-none overflow-hidden" data-testid="bp-filing-timeline">
                          <button
                            className="w-full flex items-center justify-between p-3 hover:bg-secondary/30 transition-colors text-left"
                            onClick={() => setProfileTimelineExpanded(!profileTimelineExpanded)}
                            data-testid="bp-timeline-toggle"
                          >
                            <div className="flex items-center gap-1.5">
                              {profileTimelineExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                              <Clock className="h-3.5 w-3.5 text-primary" />
                              <span className="text-xs font-semibold text-muted-foreground">Filing Timeline</span>
                            </div>
                            <span className="text-xs text-muted-foreground">{filings.length} filings</span>
                          </button>
                          {profileTimelineExpanded && (
                            <div className="px-3 pb-3">
                              <div className="relative ml-3 border-l-2 border-border pl-4 space-y-0">
                                {[...filings]
                                  .sort((a, b) => {
                                    const da = a.filing_date || a.permit_issued_date || '';
                                    const db = b.filing_date || b.permit_issued_date || '';
                                    return db.localeCompare(da);
                                  })
                                  .map((f, i) => {
                                    const statusCat = _getTimelineCategory(f.filing_status);
                                    return (
                                      <div key={i} className="relative pb-3 last:pb-0" data-testid={`bp-timeline-item-${i}`}>
                                        <div className={`absolute -left-[22px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background ${statusCat.dot}`} />
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <Badge variant="outline" className={`text-xs border ${statusCat.badge}`}>{f.filing_status}</Badge>
                                              <span className="text-xs font-mono text-muted-foreground">{f.job_filing_number || f.job_number}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                              <span className="text-xs text-muted-foreground">{f.dataset_short}</span>
                                              {f.address && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{f.address}</span>}
                                            </div>
                                          </div>
                                          <div className="text-right shrink-0 space-y-0.5">
                                            {f.filing_date && <p className="text-xs text-muted-foreground">{f.filing_date}</p>}
                                            {f.permit_issued_date && <p className="text-xs text-emerald-400">Issued: {f.permit_issued_date}</p>}
                                            {f.permit_expiration_date && <p className="text-xs text-amber-400">Exp: {f.permit_expiration_date}</p>}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                              {dobStatuses.last_checked && (
                                <p className="text-xs text-muted-foreground mt-2 text-right">Last checked: {new Date(dobStatuses.last_checked).toLocaleString()}</p>
                              )}
                            </div>
                          )}
                        </Card>
                      ) : (
                        <p className="text-xs text-muted-foreground text-center py-6" data-testid="bp-empty">
                          {jobs.length > 0
                            ? 'No DOB Open Data filings matched this project\u2019s job numbers yet. Try refreshing.'
                            : 'No DOB job numbers added yet. Add them in the Sections tab to build the profile.'}
                        </p>
                      )}

                      {/* Refresh action - same handler as Sections */}
                      {jobs.length > 0 && (
                        <div className="flex justify-end">
                          <Button size="sm" variant="ghost" onClick={() => refreshDobProject()} disabled={dobRefreshing} className="text-xs text-muted-foreground hover:text-foreground h-6 px-2" data-testid="bp-refresh-dob-btn">
                            <RefreshCw className={`h-3 w-3 mr-1 ${dobRefreshing ? 'animate-spin' : ''}`} /> {dobRefreshing ? 'Refreshing DOB...' : 'Refresh DOB Statuses'}
                          </Button>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Added DOB / ECB Violations (project-level store). Minimal
                    list + open/resolved count under the filings strip. */}
                {(() => {
                  const violations = dobStatuses.violations || [];
                  if (violations.length === 0) return null;
                  const openCount = violations.filter(v => v.is_open).length;
                  const resolvedCount = violations.length - openCount;
                  return (
                    <div className="space-y-2 pt-1" data-testid="bp-violations-section">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Violations</span>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-rose-400" data-testid="bp-violations-open-count">{openCount} Open</span>
                          <span className="text-muted-foreground">/</span>
                          <span className="text-emerald-400" data-testid="bp-violations-resolved-count">{resolvedCount} Resolved</span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        {violations.map((v, i) => (
                          <div key={v.violation_number || i} className="flex items-start justify-between gap-2 rounded-md border border-border bg-card/50 p-2" data-testid={`bp-violation-${i}`}>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge variant="outline" className={`text-xs border ${v.is_open ? 'border-rose-500/40 text-rose-400' : 'border-emerald-500/40 text-emerald-400'}`}>{v.status_label || (v.is_open ? 'Open' : 'Resolved')}</Badge>
                                <span className="text-xs font-mono text-muted-foreground">{v.violation_number}</span>
                                <span className="text-xs text-muted-foreground">{v.source}</span>
                              </div>
                              {(v.violation_type || v.description) && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{[v.violation_type, v.description].filter(Boolean).join(' - ')}</p>
                              )}
                            </div>
                            {v.issue_date && <span className="text-xs text-muted-foreground shrink-0">{v.issue_date}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Elevator / Boiler / Facade project-level stores (BIN discovery).
                    Minimal list + status-based count per source, mirroring the
                    Violations list. No detail view, no edit, no PDF export. */}
                {[
                  { key: 'elevator', label: 'Elevator', idField: 'device_number', statusOf: r => r.device_status, secondary: r => r.device_type, dateOf: r => r.status_date },
                  { key: 'boiler', label: 'Boiler', idField: 'tracking_number', statusOf: r => r.report_status, secondary: r => (r.boiler_id ? `Boiler ${r.boiler_id}` : ''), dateOf: r => r.inspection_date },
                  { key: 'facade', label: 'Facade', idField: 'control_no', statusOf: r => (r.filing_status || r.current_status), secondary: r => [r.tr6_no && `TR6 ${r.tr6_no}`, r.current_status, r.cycle && `Cycle ${r.cycle}`].filter(Boolean).join(' · '), dateOf: () => null },
                ].map(cfg => {
                  const records = dobStatuses[cfg.key] || [];
                  if (records.length === 0) return null;
                  const breakdown = statusBreakdown(records, cfg.statusOf);
                  return (
                    <div key={cfg.key} className="space-y-2 pt-1" data-testid={`bp-${cfg.key}-section`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{cfg.label}</span>
                        <div className="flex items-center gap-2 text-xs flex-wrap justify-end" data-testid={`bp-${cfg.key}-counts`}>
                          {breakdown.map(([s, c], idx) => (
                            <span key={s} className="flex items-center gap-2">
                              <span className={statusToneClass(s)}>{c} {s}</span>
                              {idx < breakdown.length - 1 && <span className="text-muted-foreground/50">·</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        {records.map((r, i) => {
                          const st = cfg.statusOf(r);
                          const sec = cfg.secondary(r);
                          const dt = cfg.dateOf(r);
                          return (
                            <div key={r[cfg.idField] || i} className="flex items-start justify-between gap-2 rounded-md border border-border bg-card/50 p-2" data-testid={`bp-${cfg.key}-${i}`}>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {st && <Badge variant="outline" className={`text-xs border ${statusBadgeClass(st)}`} data-testid={`bp-${cfg.key}-status-${i}`}>{st}</Badge>}
                                  <span className="text-xs font-mono text-muted-foreground" data-testid={`bp-${cfg.key}-id-${i}`}>{r[cfg.idField]}</span>
                                </div>
                                {sec && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{sec}</p>}
                              </div>
                              {dt && <span className="text-xs text-muted-foreground shrink-0">{dt}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* HPD Violations (project-level store, BIN->buildingid resolved).
                    Minimal list + open/closed count, mirroring DOB Violations. */}
                {(() => {
                  const hpd = dobStatuses.hpd || [];
                  if (hpd.length === 0) return null;
                  const openCount = hpd.filter(v => v.is_open).length;
                  const closedCount = hpd.length - openCount;
                  return (
                    <div className="space-y-2 pt-1" data-testid="bp-hpd-section">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">HPD Violations</span>
                        <div className="flex items-center gap-2 text-xs" data-testid="bp-hpd-counts">
                          <span className="text-rose-400" data-testid="bp-hpd-open-count">{openCount} Open</span>
                          <span className="text-muted-foreground">/</span>
                          <span className="text-emerald-400" data-testid="bp-hpd-closed-count">{closedCount} Closed</span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        {hpd.map((v, i) => (
                          <div key={v.violationid || i} className="flex items-start justify-between gap-2 rounded-md border border-border bg-card/50 p-2" data-testid={`bp-hpd-${i}`}>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge variant="outline" className={`text-xs border ${v.is_open ? 'border-rose-500/40 text-rose-400' : 'border-emerald-500/40 text-emerald-400'}`} data-testid={`bp-hpd-status-${i}`}>{v.status_label || (v.is_open ? 'Open' : 'Closed')}</Badge>
                                <span className="text-xs font-mono text-muted-foreground" data-testid={`bp-hpd-id-${i}`}>{v.violationid}</span>
                                {v.class && <span className="text-xs text-muted-foreground">Class {v.class}</span>}
                              </div>
                              {(v.novtype || v.novdescription) && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{[v.novtype, v.novdescription].filter(Boolean).join(' - ')}</p>
                              )}
                            </div>
                            {v.novissueddate && <span className="text-xs text-muted-foreground shrink-0">{v.novissueddate}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* DOT Permits (manual entry by permit number; status + expiration are fetch-only) */}
                <div className="space-y-2 pt-1" data-testid="dot-permits-section">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">DOT Permits</span>
                  {/* Add form: permit number + optional cost only */}
                  <div className="flex items-end gap-2 flex-wrap">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground">Permit number</label>
                      <Input
                        value={dotForm.permit_number}
                        onChange={e => setDotForm(f => ({ ...f, permit_number: e.target.value }))}
                        placeholder="e.g. M012024001"
                        className="h-7 w-44 bg-background border-border text-xs"
                        data-testid="dot-permit-number-input"
                        onKeyDown={e => { if (e.key === 'Enter') addDotPermit(); }}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground">Cost (optional)</label>
                      <Input
                        type="number"
                        value={dotForm.cost}
                        onChange={e => setDotForm(f => ({ ...f, cost: e.target.value }))}
                        placeholder="0.00"
                        className="h-7 w-28 bg-background border-border text-xs"
                        data-testid="dot-permit-cost-input"
                        onKeyDown={e => { if (e.key === 'Enter') addDotPermit(); }}
                      />
                    </div>
                    <Button size="sm" onClick={addDotPermit} disabled={dotAdding || !dotForm.permit_number.trim()} className="h-7 text-xs px-2.5 bg-primary text-primary-foreground" data-testid="dot-add-permit-btn">
                      <Plus className="h-3 w-3 mr-1" /> {dotAdding ? 'Adding...' : 'Add Permit'}
                    </Button>
                  </div>
                  {/* Table */}
                  {dotPermits.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic py-2" data-testid="dot-permits-empty">No DOT permits added yet.</p>
                  ) : (
                    <div className="rounded-md border border-border overflow-hidden">
                      <div className="grid grid-cols-[1.4fr_1fr_1fr_0.9fr_auto] gap-2 px-2.5 py-1.5 bg-secondary/40 text-xs font-medium text-muted-foreground">
                        <span>Permit #</span><span>Status</span><span>Expiration</span><span>Cost</span><span></span>
                      </div>
                      {dotPermits.map((p, i) => {
                        const dexp = expiryFlag(p.expiration_date, p.status);
                        return (
                        <div key={p.id} className={`grid grid-cols-[1.4fr_1fr_1fr_0.9fr_auto] gap-2 px-2.5 py-1.5 border-t border-border items-center text-xs ${expiryBorderClass(dexp)}`} data-testid={`dot-permit-row-${i}`}>
                          <span className="font-mono text-foreground truncate" title={p.permit_number} data-testid={`dot-permit-number-${i}`}>{p.permit_number}</span>
                          <span>
                            {p.status
                              ? <Badge variant="outline" className={`text-xs border ${dotStatusClass(p.status)}`} data-testid={`dot-permit-status-${i}`}>{p.status}</Badge>
                              : <span className="text-muted-foreground" data-testid={`dot-permit-status-${i}`}>No match</span>}
                          </span>
                          <span className="flex items-center gap-1.5 flex-wrap">
                            <span className={dotExpClass(p)} data-testid={`dot-permit-exp-${i}`}>{(p.expiration_date || '').split('T')[0] || '-'}</span>
                            {expiryTag(dexp, `dot-permit-exp-tag-${i}`)}
                          </span>
                          <span className="text-foreground" data-testid={`dot-permit-cost-${i}`}>{p.cost != null ? `$${Number(p.cost).toLocaleString()}` : '-'}</span>
                          <span className="flex items-center gap-0.5 justify-end">
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => refreshDotPermit(p.id)} disabled={dotRefreshingId === p.id} title="Refresh from NYC DOT" data-testid={`dot-permit-refresh-${i}`}>
                              <RefreshCw className={`h-3 w-3 ${dotRefreshingId === p.id ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-rose-400" onClick={() => deleteDotPermit(p.id)} title="Remove permit" data-testid={`dot-permit-delete-${i}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </span>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* FDNY records — fully manual, NO live FDNY data source exists.
                    Record type is picked at creation and is the only source of
                    truth for a record's branch (never inferred from the number). */}
                <div className="space-y-2 pt-1" data-testid="fdny-section">
                  <div className="flex items-center gap-1.5">
                    <Flame className="h-3.5 w-3.5 text-orange-400" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">FDNY</span>
                  </div>

                  {/* Record type picker — chosen first, drives the form below */}
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setFdnyForm(f => ({ ...FDNY_EMPTY_FORM, record_type: 'enforcement_violation', record_number: f.record_number, notes: f.notes }))}
                      className={`h-7 px-2.5 rounded-md text-xs border transition-colors ${fdnyForm.record_type === 'enforcement_violation' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:text-foreground'}`}
                      data-testid="fdny-type-enforcement-btn"
                    >Enforcement Violation</button>
                    <button
                      type="button"
                      onClick={() => setFdnyForm(f => ({ ...f, record_type: 'permit_other' }))}
                      className={`h-7 px-2.5 rounded-md text-xs border transition-colors ${fdnyForm.record_type === 'permit_other' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:text-foreground'}`}
                      data-testid="fdny-type-permit-btn"
                    >Permit / Other FDNY Record</button>
                  </div>

                  {/* Add form — fields differ by the type chosen above */}
                  {fdnyForm.record_type === 'enforcement_violation' ? (
                    <div className="flex items-end gap-2 flex-wrap" data-testid="fdny-enforcement-form">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground">Violation number</label>
                        <Input value={fdnyForm.record_number} onChange={e => setFdnyForm(f => ({ ...f, record_number: e.target.value }))} placeholder="e.g. V-123456" className="h-7 w-44 bg-background border-border text-xs" aria-label="Violation number" data-testid="fdny-violation-number-input" onKeyDown={e => { if (e.key === 'Enter') addFdnyRecord(); }} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground">Note (optional)</label>
                        <Input value={fdnyForm.notes} onChange={e => setFdnyForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional note" className="h-7 w-56 bg-background border-border text-xs" aria-label="Note" data-testid="fdny-violation-note-input" onKeyDown={e => { if (e.key === 'Enter') addFdnyRecord(); }} />
                      </div>
                      <Button size="sm" onClick={addFdnyRecord} disabled={fdnyAdding || !fdnyForm.record_number.trim()} className="h-7 text-xs px-2.5 bg-primary text-primary-foreground" data-testid="fdny-add-btn">
                        <Plus className="h-3 w-3 mr-1" /> {fdnyAdding ? 'Adding...' : 'Add Record'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-end gap-2 flex-wrap" data-testid="fdny-permit-form">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground">Type</label>
                        <Select value={fdnyForm.fdny_permit_type} onValueChange={v => setFdnyForm(f => ({ ...f, fdny_permit_type: v }))}>
                          <SelectTrigger className="h-7 w-52 bg-background border-border text-xs" aria-label="Permit type" data-testid="fdny-permit-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                          <SelectContent>{FDNY_PERMIT_TYPES.map(t => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground">Record number</label>
                        <Input value={fdnyForm.record_number} onChange={e => setFdnyForm(f => ({ ...f, record_number: e.target.value }))} placeholder="e.g. P-123456" className="h-7 w-36 bg-background border-border text-xs" aria-label="Record number" data-testid="fdny-record-number-input" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground">Status</label>
                        <Select value={fdnyForm.status} onValueChange={v => setFdnyForm(f => ({ ...f, status: v }))}>
                          <SelectTrigger className="h-7 w-32 bg-background border-border text-xs" aria-label="Status" data-testid="fdny-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{FDNY_STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground">Issue date</label>
                        <Input type="date" value={fdnyForm.issue_date} onChange={e => setFdnyForm(f => ({ ...f, issue_date: e.target.value }))} className="h-7 w-36 bg-background border-border text-xs" aria-label="Issue date" data-testid="fdny-issue-date-input" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground">Expiration (optional)</label>
                        <Input type="date" value={fdnyForm.expiration_date} onChange={e => setFdnyForm(f => ({ ...f, expiration_date: e.target.value }))} className="h-7 w-36 bg-background border-border text-xs" aria-label="Expiration date" data-testid="fdny-expiration-date-input" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground">Notes</label>
                        <Input value={fdnyForm.notes} onChange={e => setFdnyForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" className="h-7 w-56 bg-background border-border text-xs" aria-label="Notes" data-testid="fdny-notes-input" />
                      </div>
                      <Button size="sm" onClick={addFdnyRecord} disabled={fdnyAdding || !fdnyForm.record_number.trim() || !fdnyForm.fdny_permit_type} className="h-7 text-xs px-2.5 bg-primary text-primary-foreground" data-testid="fdny-add-btn">
                        <Plus className="h-3 w-3 mr-1" /> {fdnyAdding ? 'Adding...' : 'Add Record'}
                      </Button>
                    </div>
                  )}

                  {/* List — both types together, visually distinguished by type */}
                  {fdnyRecords.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic py-2" data-testid="fdny-empty">No FDNY records added yet.</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="fdny-list">
                      {fdnyRecords.map((rec, i) => {
                        const isEnf = rec.record_type === 'enforcement_violation';
                        const expanded = fdnyExpandedId === rec.id;
                        const editing = fdnyEditingId === rec.id;
                        const fexp = isEnf ? null : expiryFlag(rec.expiration_date, rec.status);
                        return (
                          <div key={rec.id} className={`rounded-md border border-border bg-card/40 text-xs ${expiryBorderClass(fexp)}`} data-testid={`fdny-row-${i}`}>
                            {editing ? (
                              <div className="px-2.5 py-2 space-y-2" data-testid={`fdny-edit-form-${i}`}>
                                {isEnf ? (
                                  <div className="flex items-end gap-2 flex-wrap">
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-muted-foreground">Violation number</label>
                                      <Input value={fdnyEditForm.record_number} onChange={e => setFdnyEditForm(f => ({ ...f, record_number: e.target.value }))} className="h-7 w-44 bg-background border-border text-xs" aria-label="Violation number" data-testid={`fdny-edit-number-${i}`} />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-muted-foreground">Note (optional)</label>
                                      <Input value={fdnyEditForm.notes} onChange={e => setFdnyEditForm(f => ({ ...f, notes: e.target.value }))} className="h-7 w-56 bg-background border-border text-xs" aria-label="Note" data-testid={`fdny-edit-notes-${i}`} />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-end gap-2 flex-wrap">
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-muted-foreground">Type</label>
                                      <Select value={fdnyEditForm.fdny_permit_type} onValueChange={v => setFdnyEditForm(f => ({ ...f, fdny_permit_type: v }))}>
                                        <SelectTrigger className="h-7 w-52 bg-background border-border text-xs" aria-label="Permit type" data-testid={`fdny-edit-type-${i}`}><SelectValue placeholder="Select type" /></SelectTrigger>
                                        <SelectContent>{FDNY_PERMIT_TYPES.map(t => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-muted-foreground">Record number</label>
                                      <Input value={fdnyEditForm.record_number} onChange={e => setFdnyEditForm(f => ({ ...f, record_number: e.target.value }))} className="h-7 w-36 bg-background border-border text-xs" aria-label="Record number" data-testid={`fdny-edit-number-${i}`} />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-muted-foreground">Status</label>
                                      <Select value={fdnyEditForm.status} onValueChange={v => setFdnyEditForm(f => ({ ...f, status: v }))}>
                                        <SelectTrigger className="h-7 w-32 bg-background border-border text-xs" aria-label="Status" data-testid={`fdny-edit-status-${i}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>{FDNY_STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-muted-foreground">Issue date</label>
                                      <Input type="date" value={fdnyEditForm.issue_date} onChange={e => setFdnyEditForm(f => ({ ...f, issue_date: e.target.value }))} className="h-7 w-36 bg-background border-border text-xs" aria-label="Issue date" data-testid={`fdny-edit-issue-${i}`} />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-muted-foreground">Expiration (optional)</label>
                                      <Input type="date" value={fdnyEditForm.expiration_date} onChange={e => setFdnyEditForm(f => ({ ...f, expiration_date: e.target.value }))} className="h-7 w-36 bg-background border-border text-xs" aria-label="Expiration date" data-testid={`fdny-edit-expiration-${i}`} />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-muted-foreground">Notes</label>
                                      <Input value={fdnyEditForm.notes} onChange={e => setFdnyEditForm(f => ({ ...f, notes: e.target.value }))} className="h-7 w-56 bg-background border-border text-xs" aria-label="Note" data-testid={`fdny-edit-notes-${i}`} />
                                    </div>
                                  </div>
                                )}
                                <div className="flex items-center gap-1.5">
                                  <Button size="sm" onClick={() => saveEditFdny(rec)} className="h-7 text-xs px-2.5 bg-primary text-primary-foreground" data-testid={`fdny-save-${i}`}><Check className="h-3 w-3 mr-1" /> Save</Button>
                                  <Button size="sm" variant="ghost" onClick={() => { setFdnyEditingId(null); setFdnyEditForm(null); }} className="h-7 text-xs px-2.5" data-testid={`fdny-cancel-${i}`}>Cancel</Button>
                                </div>
                              </div>
                            ) : (
                              <div className="px-2.5 py-1.5">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => setFdnyExpandedId(expanded ? null : rec.id)} className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`fdny-expand-${i}`}>
                                    {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                  </button>
                                  <span className="font-mono text-foreground truncate" title={rec.record_number} data-testid={`fdny-number-${i}`}>{rec.record_number}</span>
                                  {isEnf ? (
                                    <Badge variant="outline" className="text-xs border-orange-500/40 text-orange-400 shrink-0" data-testid={`fdny-type-badge-${i}`}>Enforcement Violation</Badge>
                                  ) : (
                                    <>
                                      <Badge variant="outline" className="text-xs border-sky-500/40 text-sky-400 shrink-0" data-testid={`fdny-permit-type-badge-${i}`}>{rec.fdny_permit_type}</Badge>
                                      {rec.status && <Badge variant="outline" className={`text-xs border ${fdnyStatusClass(rec.status)} shrink-0`} data-testid={`fdny-status-badge-${i}`}>{rec.status}</Badge>}
                                      {expiryTag(fexp, `fdny-exp-tag-${i}`)}
                                    </>
                                  )}
                                  <div className="ml-auto flex items-center gap-0.5 shrink-0">
                                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => startEditFdny(rec)} title="Edit" data-testid={`fdny-edit-${i}`}><Pencil className="h-3 w-3" /></Button>
                                    <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-rose-400" onClick={() => deleteFdnyRecord(rec.id)} title="Delete" data-testid={`fdny-delete-${i}`}><Trash2 className="h-3 w-3" /></Button>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 mt-1 pl-5">
                                  {isEnf ? (
                                    <Button size="sm" variant="outline" className="h-6 text-xs px-2 gap-1" onClick={() => window.open(FDNY_PORTAL_URL, '_blank', 'noopener,noreferrer')} data-testid={`fdny-portal-btn-${i}`}>
                                      <ExternalLink className="h-3 w-3" /> Verify on FDNY Portal
                                    </Button>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-xs text-amber-400/90 border border-amber-500/30 rounded px-1.5 py-0.5" data-testid={`fdny-unverified-chip-${i}`}>
                                      <AlertCircle className="h-3 w-3" /> Manually entered, not verified against city data
                                    </span>
                                  )}
                                </div>
                                {expanded && (
                                  <div className="pl-5 pt-2 mt-1.5 border-t border-border/60 space-y-1 text-muted-foreground" data-testid={`fdny-detail-${i}`}>
                                    {!isEnf && (
                                      <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5">
                                        <span>Issue: <span className="text-foreground">{(rec.issue_date || '').split('T')[0] || '-'}</span></span>
                                        <span className="flex items-center gap-1.5">Expiration: <span className={fdnyExpClass(rec)}>{(rec.expiration_date || '').split('T')[0] || '-'}</span>{expiryTag(fexp, `fdny-detail-exp-tag-${i}`)}</span>
                                      </div>
                                    )}
                                    {rec.notes ? <p className="whitespace-pre-wrap text-foreground/80">{rec.notes}</p> : <p className="italic">No notes.</p>}
                                    {!isEnf && (
                                      <span className="inline-flex items-center gap-1 text-xs text-amber-400/90" data-testid={`fdny-detail-unverified-${i}`}>
                                        <AlertCircle className="h-3 w-3" /> Manually entered, not verified against city data
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Other Jurisdiction — non-NYC municipal records (LI towns/villages,
                    county DPWs, etc.). Fully manual: NO public dataset exists for any
                    of these jurisdictions, so there is NEVER a fetch and every record
                    is permanently flagged unverified against city data. */}
                <div className="space-y-2 pt-1" data-testid="other-jurisdiction-section">
                  <div className="flex items-center gap-1.5">
                    <Landmark className="h-3.5 w-3.5 text-teal-400" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Other Jurisdiction</span>
                  </div>

                  {/* Add form — single manual form, no fetch */}
                  <div className="flex items-end gap-2 flex-wrap" data-testid="oj-form">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground">Jurisdiction</label>
                      <Select value={ojForm.jurisdiction} onValueChange={v => setOjForm(f => ({ ...f, jurisdiction: v, jurisdiction_other: v === 'Other' ? f.jurisdiction_other : '' }))}>
                        <SelectTrigger className="h-7 w-52 bg-background border-border text-xs" aria-label="Jurisdiction" data-testid="oj-jurisdiction-select"><SelectValue placeholder="Select jurisdiction" /></SelectTrigger>
                        <SelectContent>{OTHER_JURISDICTIONS.map(j => <SelectItem key={j} value={j} className="text-xs">{j}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {ojForm.jurisdiction === 'Other' && (
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground">Jurisdiction name</label>
                        <Input value={ojForm.jurisdiction_other} onChange={e => setOjForm(f => ({ ...f, jurisdiction_other: e.target.value }))} placeholder="e.g. Village of Mineola" className="h-7 w-48 bg-background border-border text-xs" data-testid="oj-jurisdiction-other-input" />
                      </div>
                    )}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground">Record type / description</label>
                      <Input value={ojForm.record_type} onChange={e => setOjForm(f => ({ ...f, record_type: e.target.value }))} placeholder="e.g. Building Permit, ZBA Variance" className="h-7 w-52 bg-background border-border text-xs" aria-label="Record type" data-testid="oj-record-type-input" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground">Record number</label>
                      <Input value={ojForm.record_number} onChange={e => setOjForm(f => ({ ...f, record_number: e.target.value }))} placeholder="e.g. BP-2024-0145" className="h-7 w-40 bg-background border-border text-xs" aria-label="Record number" data-testid="oj-record-number-input" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground">Status</label>
                      <Select value={ojForm.status} onValueChange={v => setOjForm(f => ({ ...f, status: v }))}>
                        <SelectTrigger className="h-7 w-32 bg-background border-border text-xs" aria-label="Status" data-testid="oj-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{OJ_STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground">Issue date</label>
                      <Input type="date" value={ojForm.issue_date} onChange={e => setOjForm(f => ({ ...f, issue_date: e.target.value }))} className="h-7 w-36 bg-background border-border text-xs" aria-label="Issue date" data-testid="oj-issue-date-input" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground">Expiration (optional)</label>
                      <Input type="date" value={ojForm.expiration_date} onChange={e => setOjForm(f => ({ ...f, expiration_date: e.target.value }))} className="h-7 w-36 bg-background border-border text-xs" aria-label="Expiration date" data-testid="oj-expiration-date-input" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground">Notes</label>
                      <Input value={ojForm.notes} onChange={e => setOjForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" className="h-7 w-56 bg-background border-border text-xs" aria-label="Notes" data-testid="oj-notes-input" />
                    </div>
                    <Button size="sm" onClick={addOjRecord} disabled={ojAdding || !ojForm.jurisdiction || !ojForm.record_type.trim() || !ojForm.record_number.trim() || (ojForm.jurisdiction === 'Other' && !ojForm.jurisdiction_other.trim())} className="h-7 text-xs px-2.5 bg-primary text-primary-foreground" data-testid="oj-add-btn">
                      <Plus className="h-3 w-3 mr-1" /> {ojAdding ? 'Adding...' : 'Add Record'}
                    </Button>
                  </div>

                  {/* List — every record always flagged unverified */}
                  {ojRecords.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic py-2" data-testid="oj-empty">No other-jurisdiction records added yet.</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="oj-list">
                      {ojRecords.map((rec, i) => {
                        const expanded = ojExpandedId === rec.id;
                        const editing = ojEditingId === rec.id;
                        const oexp = expiryFlag(rec.expiration_date, rec.status);
                        return (
                          <div key={rec.id} className={`rounded-md border border-border bg-card/40 text-xs ${expiryBorderClass(oexp)}`} data-testid={`oj-row-${i}`}>
                            {editing ? (
                              <div className="px-2.5 py-2 space-y-2" data-testid={`oj-edit-form-${i}`}>
                                <div className="flex items-end gap-2 flex-wrap">
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-muted-foreground">Jurisdiction</label>
                                    <Select value={ojEditForm.jurisdiction} onValueChange={v => setOjEditForm(f => ({ ...f, jurisdiction: v, jurisdiction_other: v === 'Other' ? f.jurisdiction_other : '' }))}>
                                      <SelectTrigger className="h-7 w-52 bg-background border-border text-xs" data-testid={`oj-edit-jurisdiction-${i}`}><SelectValue placeholder="Select jurisdiction" /></SelectTrigger>
                                      <SelectContent>{OTHER_JURISDICTIONS.map(j => <SelectItem key={j} value={j} className="text-xs">{j}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                  {ojEditForm.jurisdiction === 'Other' && (
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-muted-foreground">Jurisdiction name</label>
                                      <Input value={ojEditForm.jurisdiction_other} onChange={e => setOjEditForm(f => ({ ...f, jurisdiction_other: e.target.value }))} className="h-7 w-48 bg-background border-border text-xs" data-testid={`oj-edit-jurisdiction-other-${i}`} />
                                    </div>
                                  )}
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-muted-foreground">Record type / description</label>
                                    <Input value={ojEditForm.record_type} onChange={e => setOjEditForm(f => ({ ...f, record_type: e.target.value }))} className="h-7 w-52 bg-background border-border text-xs" data-testid={`oj-edit-record-type-${i}`} />
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-muted-foreground">Record number</label>
                                    <Input value={ojEditForm.record_number} onChange={e => setOjEditForm(f => ({ ...f, record_number: e.target.value }))} className="h-7 w-40 bg-background border-border text-xs" data-testid={`oj-edit-record-number-${i}`} />
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-muted-foreground">Status</label>
                                    <Select value={ojEditForm.status} onValueChange={v => setOjEditForm(f => ({ ...f, status: v }))}>
                                      <SelectTrigger className="h-7 w-32 bg-background border-border text-xs" data-testid={`oj-edit-status-${i}`}><SelectValue /></SelectTrigger>
                                      <SelectContent>{OJ_STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-muted-foreground">Issue date</label>
                                    <Input type="date" value={ojEditForm.issue_date} onChange={e => setOjEditForm(f => ({ ...f, issue_date: e.target.value }))} className="h-7 w-36 bg-background border-border text-xs" data-testid={`oj-edit-issue-${i}`} />
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-muted-foreground">Expiration (optional)</label>
                                    <Input type="date" value={ojEditForm.expiration_date} onChange={e => setOjEditForm(f => ({ ...f, expiration_date: e.target.value }))} className="h-7 w-36 bg-background border-border text-xs" data-testid={`oj-edit-expiration-${i}`} />
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-muted-foreground">Notes</label>
                                    <Input value={ojEditForm.notes} onChange={e => setOjEditForm(f => ({ ...f, notes: e.target.value }))} className="h-7 w-56 bg-background border-border text-xs" data-testid={`oj-edit-notes-${i}`} />
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Button size="sm" onClick={() => saveEditOj(rec)} className="h-7 text-xs px-2.5 bg-primary text-primary-foreground" data-testid={`oj-save-${i}`}><Check className="h-3 w-3 mr-1" /> Save</Button>
                                  <Button size="sm" variant="ghost" onClick={() => { setOjEditingId(null); setOjEditForm(null); }} className="h-7 text-xs px-2.5" data-testid={`oj-cancel-${i}`}>Cancel</Button>
                                </div>
                              </div>
                            ) : (
                              <div className="px-2.5 py-1.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <button onClick={() => setOjExpandedId(expanded ? null : rec.id)} className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`oj-expand-${i}`}>
                                    {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                  </button>
                                  <span className="font-mono text-foreground truncate" title={rec.record_number} data-testid={`oj-number-${i}`}>{rec.record_number}</span>
                                  <Badge variant="outline" className="text-xs border-teal-500/40 text-teal-400 shrink-0" data-testid={`oj-jurisdiction-badge-${i}`}>{ojJurisdictionLabel(rec)}</Badge>
                                  <span className="text-muted-foreground truncate" data-testid={`oj-record-type-${i}`}>{rec.record_type}</span>
                                  {rec.status && <Badge variant="outline" className={`text-xs border ${fdnyStatusClass(rec.status)} shrink-0`} data-testid={`oj-status-badge-${i}`}>{rec.status}</Badge>}
                                  {expiryTag(oexp, `oj-exp-tag-${i}`)}
                                  <div className="ml-auto flex items-center gap-0.5 shrink-0">
                                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => startEditOj(rec)} title="Edit" data-testid={`oj-edit-${i}`}><Pencil className="h-3 w-3" /></Button>
                                    <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-rose-400" onClick={() => deleteOjRecord(rec.id)} title="Delete" data-testid={`oj-delete-${i}`}><Trash2 className="h-3 w-3" /></Button>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 mt-1 pl-5">
                                  <span className="inline-flex items-center gap-1 text-xs text-amber-400/90 border border-amber-500/30 rounded px-1.5 py-0.5" data-testid={`oj-unverified-chip-${i}`}>
                                    <AlertCircle className="h-3 w-3" /> Manually entered, not verified against city data
                                  </span>
                                </div>
                                {expanded && (
                                  <div className="pl-5 pt-2 mt-1.5 border-t border-border/60 space-y-1 text-muted-foreground" data-testid={`oj-detail-${i}`}>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5">
                                      <span>Jurisdiction: <span className="text-foreground">{ojJurisdictionLabel(rec)}</span></span>
                                      <span>Type: <span className="text-foreground">{rec.record_type}</span></span>
                                      <span>Issue: <span className="text-foreground">{(rec.issue_date || '').split('T')[0] || '-'}</span></span>
                                      <span className="flex items-center gap-1.5">Expiration: <span className={fdnyExpClass(rec)}>{(rec.expiration_date || '').split('T')[0] || '-'}</span>{expiryTag(oexp, `oj-detail-exp-tag-${i}`)}</span>
                                    </div>
                                    {rec.notes ? <p className="whitespace-pre-wrap text-foreground/80">{rec.notes}</p> : <p className="italic">No notes.</p>}
                                    <span className="inline-flex items-center gap-1 text-xs text-amber-400/90" data-testid={`oj-detail-unverified-${i}`}>
                                      <AlertCircle className="h-3 w-3" /> Manually entered, not verified against city data
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* To-Dos */}
              <TabsContent value="todos" className="mt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{(selected.tasks || []).length} to-do(s)</p>
                  <Button size="sm" onClick={() => { setTodoFormOpen(!todoFormOpen); setEditTodo(null); }} className="bg-primary text-primary-foreground" data-testid="add-todo-btn"><Plus className="h-3 w-3 mr-1" /> Add To-Do</Button>
                </div>

                {projectReminders.length > 0 && (
                  <div className="space-y-1.5" data-testid="project-reminders">
                    <div className="flex items-center justify-between">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Action Item Suggestions <span className="font-mono tabular-nums">({projectReminders.length})</span></p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-xs text-muted-foreground hover:text-foreground border-white/15 px-2"
                        onClick={dismissAllProjectReminders}
                        data-testid="dismiss-all-project-reminders-btn"
                      >
                        Clear All
                      </Button>
                    </div>
                    {projectReminders.map(r => (
                      <div key={r.id} className="flex items-start gap-2.5 p-2.5 rounded-md border border-amber-500/20 bg-amber-500/5" data-testid={`project-reminder-${r.id}`}>
                        <AlertCircle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-amber-400 font-medium">Action item from status update</p>
                          <p className="text-sm mt-0.5">{r.action_items}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{r.created_by_name && `${r.created_by_name} · `}{new Date(r.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" className="h-6 text-xs text-primary hover:text-primary/80 gap-0.5 px-1.5" onClick={() => createTodoFromProjectReminder(r)} data-testid={`proj-reminder-create-${r.id}`}><ArrowRight className="h-3 w-3" /> To-Do</Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground" onClick={() => dismissProjectReminder(r.id)} data-testid={`proj-reminder-dismiss-${r.id}`}><X className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {todoFormOpen && (
                  <Card className="border-border border-dashed bg-transparent shadow-none">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground">New To-Do</p>
                      <Input placeholder="Title *" value={todoForm.title} onChange={e => setTodoForm({ ...todoForm, title: e.target.value })} className="bg-background border-border" data-testid="project-todo-title" />
                      <RichTextEditor placeholder="Description" value={todoForm.description} onChange={v => setTodoForm({ ...todoForm, description: v })} collapsedHeight={80} />
                      <div>
                        <Label className="text-xs text-muted-foreground">Section *</Label>
                        <Select value={todoForm.contract_section_id} onValueChange={v => setTodoForm({ ...todoForm, contract_section_id: v })}>
                          <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Select section" /></SelectTrigger>
                          <SelectContent className="bg-card border-border">{sortSections(selected.sections || []).map(s => (<SelectItem key={s.id} value={s.id}>{s.section_number ? `${s.section_number} - ` : ''}{s.section_name}{s.source_tag && s.source_tag !== 'Original Contract' ? ` [${s.source_tag}]` : ''}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Assign To</Label>
                          <Select value={todoForm.assigned_to} onValueChange={v => setTodoForm({ ...todoForm, assigned_to: v })}>
                            <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                            <SelectContent className="bg-card border-border">{users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Priority</Label>
                          <Select value={todoForm.priority} onValueChange={v => setTodoForm({ ...todoForm, priority: v })}>
                            <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-card border-border">{['High', 'Medium', 'Low'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Input type="date" placeholder="Due date" value={todoForm.due_date} onChange={e => setTodoForm({ ...todoForm, due_date: e.target.value })} className="bg-background border-border" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={createTodo} className="bg-primary text-primary-foreground" data-testid="project-todo-submit"><Plus className="h-3 w-3 mr-1" /> Create To-Do</Button>
                        <Button size="sm" variant="outline" onClick={() => setTodoFormOpen(false)}>Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {editTodo && (
                  <Card className="border-primary/30 border-dashed bg-primary/5 shadow-none">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-primary">Edit To-Do</p>
                      <Input placeholder="Title *" value={editTodoForm.title} onChange={e => setEditTodoForm({ ...editTodoForm, title: e.target.value })} className="bg-background border-border" data-testid="edit-todo-title" />
                      <RichTextEditor placeholder="Description" value={editTodoForm.description} onChange={v => setEditTodoForm({ ...editTodoForm, description: v })} collapsedHeight={80} />
                      <div>
                        <Label className="text-xs text-muted-foreground">Section *</Label>
                        <Select value={editTodoForm.contract_section_id} onValueChange={v => setEditTodoForm({ ...editTodoForm, contract_section_id: v })}>
                          <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Select section" /></SelectTrigger>
                          <SelectContent className="bg-card border-border">{sortSections(selected.sections || []).map(s => (<SelectItem key={s.id} value={s.id}>{s.section_number ? `${s.section_number} - ` : ''}{s.section_name}{s.source_tag && s.source_tag !== 'Original Contract' ? ` [${s.source_tag}]` : ''}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Assign To</Label>
                          <Select value={editTodoForm.assigned_to} onValueChange={v => setEditTodoForm({ ...editTodoForm, assigned_to: v })}>
                            <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                            <SelectContent className="bg-card border-border">{users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Priority</Label>
                          <Select value={editTodoForm.priority} onValueChange={v => setEditTodoForm({ ...editTodoForm, priority: v })}>
                            <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-card border-border">{['High', 'Medium', 'Low'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Status</Label>
                          <Select value={editTodoForm.status} onValueChange={v => setEditTodoForm({ ...editTodoForm, status: v })}>
                            <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-card border-border">{['Not Started', 'In Progress', 'Completed', 'On Hold'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Input type="date" value={editTodoForm.due_date || ''} onChange={e => setEditTodoForm({ ...editTodoForm, due_date: e.target.value })} className="bg-background border-border" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEditTodo} className="bg-primary text-primary-foreground" data-testid="save-edit-todo-btn">Save Changes</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditTodo(null)}>Cancel</Button>
                        <Button size="sm" variant="outline" className="ml-auto text-destructive border-destructive/20 hover:bg-destructive/10" onClick={() => deleteTodo(editTodo.id)} data-testid="delete-todo-btn"><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {Object.keys(todosBySection).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No to-dos yet</p>
                ) : (
                  Object.entries(todosBySection).map(([secId, todos]) => (
                    <div key={secId}>
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{getSectionLabel(secId)}</p>
                      {todos.map(t => (
                        <Card key={t.id} className="border-border bg-background shadow-none mb-1.5 cursor-pointer hover:border-primary/30 transition-colors" onClick={() => openEditTodo(t)} data-testid={`todo-card-${t.id}`}>
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{t.title}</p>
                                {t.description && <div className="mt-0.5"><RichTextDisplay value={t.description} collapsedHeight={20} /></div>}
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  {t.assignee_name && (
                                    <span className="flex items-center gap-0.5 text-xs text-muted-foreground"><User className="h-2.5 w-2.5" />{t.assignee_name}</span>
                                  )}
                                  {t.due_date && (
                                    <span className="flex items-center gap-0.5 text-xs text-muted-foreground"><Calendar className="h-2.5 w-2.5" />{t.due_date}</span>
                                  )}
                                  {t.source_meeting_note_id && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setHighlightNoteId(t.source_meeting_note_id);
                                        setActiveTab('meeting-notes');
                                      }}
                                      className="flex items-center gap-0.5 text-xs text-primary hover:underline"
                                      data-testid={`todo-source-link-${t.id}`}
                                      title="Jump to originating meeting note"
                                    >
                                      <MessageSquareText className="h-2.5 w-2.5" /> Source
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge variant="outline" className={`text-xs ${t.priority === 'High' ? 'text-destructive border-destructive/20' : t.priority === 'Medium' ? 'text-amber-400 border-amber-500/20' : ''}`}>{t.priority}</Badge>
                                <Badge variant="outline" className="text-xs">{t.status}</Badge>
                                <Pencil className="h-3 w-3 text-muted-foreground" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ))
                )}
              </TabsContent>

              {/* Status Log */}
              <TabsContent value="status" className="mt-3 space-y-3">
                <div className="space-y-2">
                  <RichTextEditor placeholder="Status update..." ariaLabel="Status log entry" value={statusText} onChange={v => setStatusText(v)} collapsedHeight={80} data-testid="status-text-input" />
                  <Input placeholder="Action items (optional)" value={actionItems} onChange={e => setActionItems(e.target.value)} className="bg-background border-border" />
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => addStatusEntry(false)} className="bg-primary text-primary-foreground" data-testid="add-status-btn"><Plus className="h-3 w-3 mr-1" /> Add Status</Button>
                    <Button size="sm" variant="outline" onClick={() => addStatusEntry(true)} className="border-primary/40 text-primary hover:bg-primary/10" data-testid="add-status-log-time-btn"><Clock className="h-3 w-3 mr-1" /> Add Status + Log Time</Button>
                  </div>
                </div>
                {editStatusEntry && (
                  <Card className="border-primary/30 border-dashed bg-primary/5 shadow-none" data-testid="edit-status-entry-form">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-primary">Edit Status Entry <span className="font-normal text-muted-foreground">· originally posted {new Date(editStatusEntry.created_at).toLocaleString()}</span></p>
                      <RichTextEditor placeholder="Status text *" value={editStatusForm.status_text} onChange={v => setEditStatusForm({ ...editStatusForm, status_text: v })} collapsedHeight={80} data-testid="edit-status-text-input" />
                      <Input placeholder="Action items" value={editStatusForm.action_items} onChange={e => setEditStatusForm({ ...editStatusForm, action_items: e.target.value })} className="bg-background border-border" data-testid="edit-status-action-input" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEditStatusEntry} className="bg-primary text-primary-foreground" data-testid="save-edit-status-btn">Save Changes</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditStatusEntry(null)}>Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                <div className="space-y-2">
                  {(selected.status_log || []).map(s => (
                    <Card key={s.id} className="border-border bg-background shadow-none hover:border-primary/30 transition-colors" data-testid={`status-entry-${s.id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0 overflow-hidden break-words">
                            <RichTextDisplay value={s.status_text} collapsedHeight={40} />
                            {s.action_items && <p className="text-xs text-muted-foreground mt-1">Action: {s.action_items}</p>}
                            <p className="text-xs text-muted-foreground mt-1">{new Date(s.created_at).toLocaleString()}{s.updated_at ? ` (edited ${new Date(s.updated_at).toLocaleString()})` : ''}</p>
                          </div>
                          <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-primary" onClick={() => openEditStatusEntry(s)} data-testid={`edit-status-entry-${s.id}`} title="Edit entry">
                            <Pencil className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* Meeting Notes */}
              <TabsContent value="meeting-notes" className="mt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{meetingNotes.length} meeting note(s)</p>
                  <Button size="sm" onClick={() => { setMeetingNoteFormOpen(o => !o); setEditMeetingNote(null); }} className="bg-primary text-primary-foreground" data-testid="add-meeting-note-btn"><Plus className="h-3 w-3 mr-1" /> Add Note</Button>
                </div>
                {meetingNoteFormOpen && (
                  <MeetingNoteCreateForm
                    projectId={selected.id}
                    onSaved={handleMeetingNoteSaved}
                    onCancel={() => setMeetingNoteFormOpen(false)}
                    onLogTime={handleMeetingNoteLogTime}
                  />
                )}
                {editMeetingNote && (
                  <MeetingNoteEditForm
                    key={editMeetingNote.id}
                    projectId={selected.id}
                    note={editMeetingNote}
                    onSaved={handleMeetingNoteEdited}
                    onCancel={() => setEditMeetingNote(null)}
                  />
                )}
                {meetingNotes.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No meeting notes yet</p>
                ) : (
                  meetingNotes.map(n => (
                    <Card key={n.id} id={`mn-${n.id}`} className={`border-border bg-background shadow-none hover:border-primary/30 transition-all ${n.client_id ? 'border-l-4 border-l-blue-500' : ''}`} data-testid={`meeting-note-${n.id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <Badge variant="outline" className="text-xs font-mono">{n.date}</Badge>
                              {n.client_id && (
                                <Badge className="text-xs bg-blue-500/15 text-blue-600 hover:bg-blue-500/15 border border-blue-500/30" data-testid={`meeting-note-client-badge-${n.id}`}>
                                  <MessageSquareText className="h-2.5 w-2.5 mr-0.5" />
                                  Client Meeting{n.client_name ? ` · ${n.client_name}` : ''}
                                </Badge>
                              )}
                              <MeetingNoteTodosBadge note={n} />
                              {n.attendees && <span className="text-xs text-muted-foreground">Attendees: {n.attendees}</span>}
                            </div>
                            <RichTextDisplay value={n.notes} collapsedHeight={80} />
                            <p className="text-xs text-muted-foreground mt-1.5">{n.created_by_name && `${n.created_by_name} · `}{new Date(n.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => setTodoFromNote(n)} data-testid={`create-todo-from-note-${n.id}`} title="Create to-do"><ListTodo className="h-3 w-3" /></Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => openEditMeetingNote(n)} aria-label="Edit meeting note" title="Edit meeting note" data-testid={`edit-meeting-note-${n.id}`}><Pencil className="h-3 w-3" /></Button>
                            {!n.client_id && (
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => deleteMeetingNote(n.id)} aria-label="Delete meeting note" title="Delete meeting note" data-testid={`delete-meeting-note-${n.id}`}><Trash2 className="h-3 w-3" /></Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              {/* Documents / Attachments */}
              <TabsContent value="docs" className="mt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{projectAttachments.length} attachment(s)</p>
                  <Button size="sm" onClick={() => { setAttachFormOpen(!attachFormOpen); setAttachName(''); setAttachUploaded(null); }} className="bg-primary text-primary-foreground" data-testid="add-project-attachment-btn"><Upload className="h-3 w-3 mr-1" /> Attach File</Button>
                </div>

                {attachFormOpen && (
                  <Card className="border-border border-dashed bg-transparent shadow-none">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground">New Attachment</p>
                      <FileUpload
                        onUploadComplete={(data) => {
                          setAttachUploaded(data);
                          if (!attachName && data.file_name) {
                            setAttachName(data.file_name.replace(/\.[^/.]+$/, ''));
                          }
                        }}
                      />
                      {attachUploaded && (
                        <>
                          <div>
                            <Label className="text-xs text-muted-foreground">Document Name</Label>
                            <Input value={attachName} onChange={e => setAttachName(e.target.value)} className="bg-background border-border" placeholder="Document name" data-testid="attachment-name-input" />
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={async () => {
                              if (!attachName.trim()) return toast.error('Document name required');
                              try {
                                await api.post('/documents', {
                                  name: attachName,
                                  category: 'Other',
                                  project_id: selected.id,
                                  file_url: attachUploaded.file_url,
                                  file_id: attachUploaded.file_id,
                                  file_name: attachUploaded.file_name,
                                  file_size: attachUploaded.file_size,
                                  content_type: attachUploaded.content_type,
                                });
                                toast.success('Attachment added');
                                setAttachFormOpen(false);
                                setAttachUploaded(null);
                                setAttachName('');
                                fetchAttachments();
                              } catch { toast.error('Error adding attachment'); }
                            }} className="bg-primary text-primary-foreground" data-testid="save-project-attachment-btn"><Plus className="h-3 w-3 mr-1" /> Save</Button>
                            <Button size="sm" variant="outline" onClick={() => { if (attachUploaded?.file_id) api.delete(`/upload/${attachUploaded.file_id}`).catch(() => {}); setAttachFormOpen(false); setAttachUploaded(null); }}>Cancel</Button>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}

                {projectAttachments.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No attachments yet</p>
                ) : (
                  projectAttachments.map(d => {
                    const fullUrl = d.file_url ? (d.file_url.startsWith('http') ? d.file_url : `${process.env.REACT_APP_BACKEND_URL}${d.file_url}`) : null;
                    const isImage = d.content_type?.startsWith('image/');
                    return (
                      <div key={d.id} className="flex items-center justify-between py-2 px-2 border border-border rounded-md hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => setPreviewAttachment(d)} data-testid={`project-attachment-${d.id}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {isImage && fullUrl ? (
                            <img src={fullUrl} alt="" className="h-8 w-8 rounded object-cover border border-border shrink-0" />
                          ) : (
                            <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{d.name}</p>
                            <p className="text-xs text-muted-foreground">{d.file_name || ''}{d.file_size ? ` · ${(d.file_size / 1024).toFixed(0)} KB` : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={(e) => { e.stopPropagation(); setPreviewAttachment(d); }} data-testid={`preview-attachment-${d.id}`}><Eye className="h-3 w-3" /></Button>
                          {fullUrl && (
                            <a href={fullUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground"><Download className="h-3 w-3" /></Button>
                            </a>
                          )}
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm('Remove this attachment?')) return;
                            try { await api.delete(`/documents/${d.id}`); toast.success('Attachment removed'); fetchAttachments(); }
                            catch { toast.error('Error removing attachment'); }
                          }} data-testid={`delete-attachment-${d.id}`}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </TabsContent>
      </Tabs>
      )}

        <DocumentPreviewModal
          open={!!previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          doc={previewAttachment}
        />

        <CreateTodoFromNoteDialog
          open={!!todoFromNote}
          onOpenChange={(o) => { if (!o) setTodoFromNote(null); }}
          note={todoFromNote}
          users={users}
          onCreated={async () => {
            // Refresh project detail so the Tasks tab updates
            if (selected?.id) {
              try {
                const r = await api.get(`/projects/${selected.id}`);
                if (onRefresh) onRefresh(r.data);
              } catch { /* best-effort */ }
            }
          }}
        />
        <QuickLogTimeSheet
          open={quickLogOpen}
          onOpenChange={setQuickLogOpen}
          entityType="project"
          entityId={selected?.id}
          entityLabel={selected ? `${selected.project_number || ''} - ${selected.name || ''}` : ''}
          initialNotes={quickLogNotes}
          sourceStatusLogId={quickLogStatusId}
          sourceSurface={quickLogSurface}
          onLogged={refreshSelected}
        />

      {/* NYC Open Data bulk-discovery review modal */}
      <Dialog open={discoverOpen} onOpenChange={setDiscoverOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="dob-discover-modal">
          <DialogHeader>
            <DialogTitle>NYC Open Data{(discoverResults.bin_number || selected?.bin_number) ? ` - BIN ${discoverResults.bin_number || selected?.bin_number}` : ''}</DialogTitle>
            <DialogDescription>Review discovered DOB records and add the ones you want. Nothing is added until you click Add Selected.</DialogDescription>
          </DialogHeader>
          {selected?.bin_address_validation && selected.bin_address_validation.status !== 'match' && (
            <p className="flex items-start gap-1.5 text-xs text-amber-400/90 border border-amber-500/30 bg-amber-500/10 rounded-md px-2 py-1.5" data-testid="dob-modal-bin-address-warning">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> BIN may not match address on file, verify before relying on this data
            </p>
          )}
          <ScrollArea className="flex-1 -mx-2 px-2">
            {discovering ? (
              <div className="py-16 text-center text-sm text-muted-foreground animate-pulse" data-testid="dob-discover-loading">Querying NYC Open Data...</div>
            ) : (
              <div className="space-y-4">
                {[
                  { key: 'dob_filings', label: 'DOB Filings', prefix: 'f:' },
                  { key: 'dob_violations', label: 'DOB Violations', prefix: 'dv:' },
                  { key: 'ecb_violations', label: 'DOB ECB Violations', prefix: 'ev:' },
                  { key: 'elevator', label: 'Elevator', prefix: 'el:' },
                  { key: 'boiler', label: 'Boiler', prefix: 'bo:' },
                  { key: 'facade', label: 'Facade', prefix: 'fa:' },
                  { key: 'hpd', label: 'HPD Violations', prefix: 'hp:' },
                ].map(group => {
                  const items = discoverResults[group.key] || [];
                  const isFiling = group.key === 'dob_filings';
                  const isNew = group.key === 'elevator' || group.key === 'boiler' || group.key === 'facade';
                  const isHpd = group.key === 'hpd';
                  return (
                    <div key={group.key} data-testid={`dob-group-${group.key}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{group.label} <span className="text-muted-foreground/70">({items.length})</span></span>
                        {items.length > 0 && (
                          <button type="button" className="text-xs text-primary hover:underline" onClick={() => toggleSelectAllGroup(items, group.prefix)} data-testid={`dob-selectall-${group.key}`}>Select all</button>
                        )}
                      </div>
                      {isHpd && discoverWarnings.hpd && (
                        <p className="flex items-start gap-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-2 mb-1.5" data-testid="dob-hpd-warning">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {discoverWarnings.hpd}
                        </p>
                      )}
                      {items.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic pb-1">No new records found.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {items.map((it, i) => {
                            const k = group.prefix + discoverIdOf(it);
                            return (
                              <label key={k} className="flex items-start gap-2 rounded-md border border-border bg-card/50 p-2 cursor-pointer hover:border-primary/40 transition-colors" data-testid={`dob-item-${group.key}-${i}`}>
                                <Checkbox checked={selectedKeys.has(k)} onCheckedChange={() => toggleDiscoverKey(k)} className="mt-0.5" data-testid={`dob-check-${group.key}-${i}`} />
                                <div className="min-w-0 flex-1">
                                  {isHpd ? (
                                    <>
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-xs font-mono text-foreground">{it.violationid}</span>
                                        <Badge variant="outline" className={`text-xs border ${it.is_open ? 'border-rose-500/40 text-rose-400' : 'border-emerald-500/40 text-emerald-400'}`}>{it.status_label || (it.is_open ? 'Open' : 'Closed')}</Badge>
                                        {it.class && <span className="text-xs text-muted-foreground">Class {it.class}</span>}
                                      </div>
                                      {(it.novtype || it.novdescription) && (
                                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{[it.novtype, it.novdescription].filter(Boolean).join(' - ')}</p>
                                      )}
                                      {it.novissueddate && <p className="text-xs text-muted-foreground/70 mt-0.5">{it.novissueddate}</p>}
                                    </>
                                  ) : isNew ? (
                                    <>
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-xs font-mono text-foreground">{discoverIdOf(it)}</span>
                                        <Badge variant="outline" className="text-xs border-border text-muted-foreground">{it.device_status || it.report_status || it.filing_status || it.current_status || '—'}</Badge>
                                        <span className="text-xs text-muted-foreground">{it.source}</span>
                                      </div>
                                      {(() => {
                                        const sec = group.key === 'elevator' ? it.device_type
                                          : group.key === 'boiler' ? (it.boiler_id ? `Boiler ${it.boiler_id}` : '')
                                          : [it.tr6_no && `TR6 ${it.tr6_no}`, it.current_status, it.cycle && `Cycle ${it.cycle}`].filter(Boolean).join(' · ');
                                        return sec ? <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{sec}</p> : null;
                                      })()}
                                      {(it.status_date || it.inspection_date) && <p className="text-xs text-muted-foreground/70 mt-0.5">{it.status_date || it.inspection_date}</p>}
                                    </>
                                  ) : (
                                    <>
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-xs font-mono text-foreground">{isFiling ? (it.job_filing_number || it.job_number) : it.violation_number}</span>
                                        <Badge variant="outline" className="text-xs border-border text-muted-foreground">{isFiling ? (it.filing_status || it.dataset_short) : (it.status_label || (it.is_open ? 'Open' : 'Resolved'))}</Badge>
                                        {isFiling ? (it.dataset_short && <span className="text-xs text-muted-foreground">{it.dataset_short}</span>) : (it.source && <span className="text-xs text-muted-foreground">{it.source}</span>)}
                                      </div>
                                      {(isFiling ? it.address : (it.violation_type || it.description)) && (
                                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{isFiling ? it.address : [it.violation_type, it.description].filter(Boolean).join(' - ')}</p>
                                      )}
                                      {(it.filing_date || it.issue_date) && <p className="text-xs text-muted-foreground/70 mt-0.5">{it.filing_date || it.issue_date}</p>}
                                    </>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {((discoverResults.dob_filings?.length || 0) + (discoverResults.dob_violations?.length || 0) + (discoverResults.ecb_violations?.length || 0) + (discoverResults.elevator?.length || 0) + (discoverResults.boiler?.length || 0) + (discoverResults.facade?.length || 0) + (discoverResults.hpd?.length || 0)) === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-10" data-testid="dob-discover-empty">No new records found for this BIN. Anything discovered is already linked to this project.</p>
                )}
              </div>
            )}
          </ScrollArea>
          <DialogFooter className="border-t border-border pt-3">
            <div className="flex items-center justify-between w-full">
              <span className="text-xs text-muted-foreground" data-testid="dob-selected-count">{selectedKeys.size} selected</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDiscoverOpen(false)} data-testid="dob-discover-cancel">Cancel</Button>
                <Button size="sm" onClick={addDiscoverSelected} disabled={addingSelected || selectedKeys.size === 0} className="bg-primary text-primary-foreground" data-testid="dob-add-selected-btn">{addingSelected ? 'Adding...' : 'Add Selected'}</Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
